import { Fragment, useEffect, useRef, useState } from "react";
import { SocialPackTab } from "./SocialPackTab.js";
import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Image as ImageIcon, AlertTriangle, Loader2, RefreshCw, Pencil, Link2, FileText, ExternalLink } from "lucide-react";
import { RichTextEditor } from "../../components/RichTextEditor.js";
import { SelectionEditor } from "./SelectionEditor.js";
import {
  api,
  ApiError,
  type ActionResult,
  type KeywordAssetView,
  type KeywordDetail,
  type Priority,
  type ProduceJobView,
  type AccuracyFinding,
  type Status,
} from "../../lib/api.js";
import { CoverageBadge, CoverageCard } from "../../components/ui/CoverageBadge.js";
import { ClusterPicker } from "../../components/ui/ClusterPicker.js";
import { summariseAiAssign } from "../../lib/api.js";
import type { CoverageResult, DuplicateInfo } from "../../lib/api.js";
import { VideoStudioTab } from "./VideoStudioTab.js";
import { MaestroHandoff } from "./MaestroHandoff.js";
import { RegenerateModal } from "./RegenerateModal.js";

/**
 * Slide-out panel anchored to the right edge of the viewport. Renders:
 *   - Header (keyword + cluster + status)
 *   - Action toolbar (Enrich, Brief, Produce, Distribute) with inline feedback
 *   - Tabbed content (Overview · SERP · Brief · Article · Extracts)
 *
 * The panel manages its own action state — invalidates queries on success so
 * the table behind it refreshes automatically.
 */
export function KeywordDetailPanel({ keywordId, onClose }: { keywordId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isPending, error } = useQuery({
    queryKey: ["pipeline", "keyword", keywordId],
    queryFn: () => api.getKeyword(keywordId),
  });
  // Coverage is cheap (no LLM, no spend) — auto-check on open so the operator
  // sees "we already have a live page for this" before producing.
  const coverageQuery = useQuery({
    queryKey: ["pipeline", "keyword", keywordId, "coverage"],
    queryFn: () => api.getKeywordCoverage(keywordId),
    staleTime: 60_000,
  });
  const [tab, setTab] = useState<Tab>("overview");

  // ESC to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["pipeline", "keyword", keywordId] });
    qc.invalidateQueries({ queryKey: ["pipeline", "keywords"] });
    qc.invalidateQueries({ queryKey: ["pipeline", "clusters"] });
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-ink/20 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />
      {/* Panel */}
      <aside className="fixed inset-y-0 right-0 z-50 flex w-full max-w-3xl flex-col bg-white shadow-2xl">
        <header className="flex items-start justify-between gap-4 border-b border-line p-5">
          <div className="min-w-0">
            {data?.keyword ? (
              <>
                <div className="truncate text-lg font-semibold" dir={data.keyword.language === "he" ? "rtl" : "ltr"}>
                  {data.keyword.keyword}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-1 gap-y-1 truncate text-xs text-muted">
                  <span>
                    {data.keyword.id}{data.cluster && ` · ${data.cluster.nameEn}`}{" · "}
                    <span className="font-medium">{data.keyword.status}</span>
                    {data.live?.unpublishedChanges && (
                      <span
                        className="ml-2 rounded-full bg-amber-100 px-1.5 py-0.5 text-2xs font-medium text-amber-700"
                        title="The pipeline version is newer than what's live — re-publish to push your changes."
                      >
                        unpublished changes
                      </span>
                    )}
                    {data.keyword.pillarPage && <span className="ml-2 text-brand">pillar ●</span>}
                  </span>
                  <CoverageBadge coverage={coverageQuery.data?.coverage} />
                </div>
              </>
            ) : isPending ? (
              <div className="h-6 w-48 animate-pulse rounded bg-line" />
            ) : null}
          </div>
          <button onClick={onClose} className="btn-ghost shrink-0" title="Close (Esc)">
            ✕
          </button>
        </header>

        {error && (
          <div className="border-b border-line bg-danger/10 p-4 text-sm text-danger">
            {(error as Error).message}
          </div>
        )}

        {data?.keyword && (
          <ActionToolbar
            keywordId={keywordId}
            detail={data}
            onAfterAction={invalidate}
          />
        )}

        <nav className="flex gap-1 border-b border-line px-5 py-2">
          {TABS.map((t) => (
            <TabButton key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>
              {t.label}
            </TabButton>
          ))}
        </nav>

        <div className="flex-1 overflow-y-auto p-5">
          {data && (
            <>
              {tab === "overview" && <OverviewTab detail={data} coverage={coverageQuery.data?.coverage} loadingCoverage={coverageQuery.isPending} />}
              {tab === "serp" && <SerpTab serp={data.keyword.serp} trends={data.keyword.trends} language={data.keyword.language} />}
              {tab === "brief" && <BriefTab brief={data.keyword.brief} language={data.keyword.language} doNotMention={data.doNotMention} keywordId={keywordId} onChanged={invalidate} />}
              {tab === "article" && <ArticleTab detail={data} keywordId={keywordId} onChanged={invalidate} />}
              {tab === "extracts" && <ExtractsTab detail={data} />}
              {tab === "assets" && <AssetsTab keywordId={keywordId} detail={data} />}
              {tab === "studio" && <VideoStudioTab keywordId={keywordId} detail={data} onAfterAction={invalidate} />}
              {tab === "social" && <SocialPackTab keywordId={keywordId} />}
            </>
          )}
        </div>
      </aside>
    </>
  );
}

type Tab = "overview" | "serp" | "brief" | "article" | "extracts" | "assets" | "studio" | "social";
const TABS: { id: Tab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "serp", label: "SERP" },
  { id: "brief", label: "Brief" },
  { id: "article", label: "Article" },
  { id: "extracts", label: "Extracts" },
  { id: "assets", label: "Assets" },
  { id: "studio", label: "Studio" },
  { id: "social", label: "Social" },
];

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={
        active
          ? "rounded-md bg-brand/10 px-3 py-1.5 text-sm font-medium text-brand"
          : "rounded-md px-3 py-1.5 text-sm font-medium text-muted hover:bg-line hover:text-ink"
      }
    >
      {children}
    </button>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Action toolbar — buttons + per-action status / streaming progress
// ───────────────────────────────────────────────────────────────────────────

function ActionToolbar({
  keywordId,
  detail,
  onAfterAction,
}: {
  keywordId: string;
  detail: KeywordDetail;
  onAfterAction: () => void;
}) {
  const k = detail.keyword;
  const qc = useQueryClient();
  const [regenOpen, setRegenOpen] = useState(false);
  const [banner, setBanner] = useState<{ kind: "ok" | "warn" | "error"; text: string; link?: { label: string; url: string } } | null>(null);
  const [review, setReview] = useState<{ findings: AccuracyFinding[]; summary: string; highCount: number; mediumCount: number; estimatedCostUsd: number | null } | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  /**
   * A brief or produce the server refused because a live blog article already
   * targets this keyword. Held in state rather than raised as a
   * `window.confirm` on purpose: the failure this gate exists for was an
   * operator not SEEING the existing article, and an alert box cannot carry a
   * link. The card names the page, links to it, and only then offers to go on.
   */
  const [duplicate, setDuplicate] = useState<
    { info: DuplicateInfo; message: string; retry: () => void; proceedLabel?: string } | null
  >(null);

  const runReview = async () => {
    setReviewing(true);
    setBanner(null);
    try {
      const r = await api.reviewAccuracy(keywordId);
      setReview(r);
      setBanner({
        kind: r.highCount > 0 ? "error" : r.mediumCount > 0 ? "warn" : "ok",
        text: `Accuracy review · ${r.findings.length} findings (${r.highCount} high, ${r.mediumCount} med) · $${r.estimatedCostUsd?.toFixed(3) ?? "?"}`,
      });
      qc.invalidateQueries({ queryKey: ["errors"] });
    } catch (e) {
      setBanner({ kind: "error", text: (e as Error).message });
    } finally {
      setReviewing(false);
    }
  };

  // Persistent produce progress — polled from the server so it survives a page
  // refresh / reopening the item, and reflects a run that's still going.
  const produceStatusQuery = useQuery({
    queryKey: ["produce-status", keywordId],
    queryFn: () => api.getProduceStatus(keywordId),
    refetchInterval: (q) => (q.state.data?.status === "producing" ? 2000 : false),
    refetchOnWindowFocus: true,
  });
  const job = produceStatusQuery.data;

  /**
   * The brief runs in the background too — it takes ~2 minutes and the gateway
   * used to hang up at 504 while the server finished the work anyway, which
   * looked exactly like a failure and wasn't one. Polling is the only honest
   * signal, and it survives a refresh or a closed panel the same way produce does.
   */
  const briefStatusQuery = useQuery({
    queryKey: ["brief-status", keywordId],
    queryFn: () => api.briefStatus(keywordId),
    refetchInterval: (q) => (q.state.data?.status === "running" ? 3000 : false),
    refetchOnWindowFocus: true,
  });
  const briefJob = briefStatusQuery.data;
  const prevBriefStatus = useRef<string | undefined>(undefined);
  useEffect(() => {
    const s = briefJob?.status;
    if (prevBriefStatus.current === "running" && s === "done") {
      onAfterAction();
      setBanner({ kind: "ok", text: briefJob?.summary ?? "Brief generated" });
    } else if (prevBriefStatus.current === "running" && s === "error") {
      onAfterAction();
      setBanner({ kind: "error", text: briefJob?.error ?? "Brief failed" });
    }
    prevBriefStatus.current = s;
  }, [briefJob?.status]);

  // When a run finishes (even if we only learned via polling), refresh the
  // article + stamp a banner.
  const prevStatus = useRef<string | undefined>(undefined);
  useEffect(() => {
    const s = job?.status;
    if (prevStatus.current === "producing" && s === "done") {
      onAfterAction();
      setBanner({ kind: "ok", text: `Produced · ${job?.summary?.totalWordCount ?? "?"}w · $${job?.summary?.estimatedCostUsd?.toFixed(4) ?? "?"} · ${job?.summary?.modelUsed ?? ""}` });
    } else if (prevStatus.current === "producing" && s === "error") {
      onAfterAction();
      setBanner({ kind: "error", text: job?.error ?? "Production failed" });
    }
    prevStatus.current = s;
  }, [job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persistent image-generation progress — same rationale as produce: survives a
  // refresh/reopen and reflects a still-running job (incl. the produce→images
  // auto-chain, which starts a job with no click here).
  const imagesStatusQuery = useQuery({
    queryKey: ["images-status", keywordId],
    queryFn: () => api.getImagesStatus(keywordId),
    refetchInterval: (q) => (q.state.data?.status === "generating" ? 2000 : false),
    refetchOnWindowFocus: true,
  });
  const imgJob = imagesStatusQuery.data;
  const imagesInFlight = imgJob?.status === "generating";

  const prevImgStatus = useRef<string | undefined>(undefined);
  useEffect(() => {
    const s = imgJob?.status;
    if (prevImgStatus.current === "generating" && s === "done") {
      onAfterAction();
      const g = imgJob?.summary?.generated ?? 0;
      const sk = imgJob?.summary?.skipped ?? 0;
      const warn = imgJob?.summary?.warnings?.length ?? 0;
      setBanner({
        kind: warn > 0 ? "warn" : "ok",
        text: `Images done · ${g} generated, ${sk} skipped${warn > 0 ? ` · ${warn} warning(s)` : ""}`,
      });
    } else if (prevImgStatus.current === "generating" && s === "error") {
      onAfterAction();
      setBanner({ kind: "error", text: imgJob?.error ?? "Image generation failed" });
    }
    prevImgStatus.current = s;
  }, [imgJob?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Forward-flow "Generate images": fills only the missing images for the produced article. */
  const startImages = async () => {
    setBanner(null);
    try {
      const r = await api.generateImages(keywordId);
      if (!r.ok) throw new Error(r.error ?? "Image generation failed");
      setBanner({ kind: "ok", text: r.summary ?? "Image generation started" });
      setTimeout(() => qc.invalidateQueries({ queryKey: ["images-status", keywordId] }), 300);
    } catch (e) {
      setBanner({ kind: "error", text: (e as Error).message });
    }
  };

  /**
   * "Merge into this article" — the duplicate card's way out that is neither
   * "keep the old one" nor "publish a second one". Points this keyword at the
   * live document (merge-into-live), so the ordinary Publish updates that page
   * in place: same URL, original publish date, Studio fields kept. This button
   * publishes nothing; the operator reviews and presses Publish.
   */
  const [merging, setMerging] = useState(false);
  const mergeIntoLive = async (info: DuplicateInfo) => {
    if (!info.sanityId) return;
    const ok = window.confirm(
      `Merge this article into "${info.title ?? info.url}"?\n\n` +
        "Publishing will then REPLACE that live page's body and title with this article, at the same URL and with its original publish date. " +
        "Nothing is published now. Afterwards, press Publish (and read its preview) when you are ready.",
    );
    if (!ok) return;
    setMerging(true);
    try {
      const r = await api.mergeIntoLive(keywordId, info.sanityId);
      if (!r.ok) throw new Error(r.error ?? "Merge failed");
      setDuplicate(null);
      setBanner({ kind: "ok", text: `${r.summary ?? "Linked to the live article."} Press Publish to update it.` });
      onAfterAction();
    } catch (e) {
      setBanner({ kind: "error", text: (e as Error).message });
    } finally {
      setMerging(false);
    }
  };

  /**
   * Publish, with the image-loss guard woven in. On the first attempt we let the
   * server decide: if the body still has unfilled {{IMAGE:}} markers it returns a
   * 409 (code "unfilled_image_markers") instead of silently dropping them. We turn
   * that into an explicit choice — generate images now, or publish without them.
   */
  const startPublish = async () => {
    // Guard: re-publishing a live doc is a deliberate overwrite — keep the existing confirm.
    if (
      detail.live?.isLive &&
      !window.confirm(
        `This article is already LIVE${detail.live.url ? ` at ${detail.live.url}` : ""}.\n\n` +
          "Publishing again overwrites the live document (Studio edits are preserved and the original publish date is kept). Continue?",
      )
    ) {
      return;
    }
    /**
     * Taxonomy is normally PRESERVED on a re-publish so a Studio correction
     * survives. That also makes a taxonomy we got wrong permanent — a cupping
     * article kept a CBT tag through a re-publish because the code cannot tell a
     * human's fix from its own mistake. Only the operator knows which it is, so
     * only the operator is asked, and only when there is something to overwrite.
     */
    const refreshTaxonomy =
      !!detail.live?.isLive &&
      window.confirm(
        "Also re-resolve categories and tags?\n\n" +
          "OK      → overwrite the live doc's taxonomy with freshly matched values\n" +
          "Cancel → keep what's on the doc (this is what protects your Studio edits)",
      );
    setBanner(null);
    setPublishing(true);
    try {
      const r = await api.publishToSanity(keywordId, refreshTaxonomy ? { refreshTaxonomy: true } : {});
      if (!r.ok) throw new Error(r.error ?? "Publish failed");
      setBanner({ kind: "ok", text: r.summary ?? "Published" });
      onAfterAction();
    } catch (e) {
      const imageCode = e instanceof ApiError ? e.data?.code : undefined;
      if (e instanceof ApiError && (imageCode === "unfilled_image_markers" || imageCode === "missing_hero_image")) {
        const n = (e.data?.unfilledImageMarkers as number | undefined) ?? 0;
        const goImages = window.confirm(
          imageCode === "missing_hero_image"
            ? `This article has no hero image — it would publish without a main image.\n\n` +
                `OK  → cancel publish and Generate images now (recommended)\n` +
                `Cancel → publish anyway, without a main image`
            : `This article has ${n} in-content image placeholder(s) with no generated image.\n\n` +
                `Publishing now DROPS them silently.\n\n` +
                `OK  → cancel publish and Generate images now (recommended)\n` +
                `Cancel → publish anyway, without in-content images`,
        );
        if (goImages) {
          await startImages();
          return;
        }
        // Operator chose to publish without images — re-issue, bypassing the guard.
        try {
          const r2 = await api.publishToSanity(keywordId, { allowMissingImages: true, ...(refreshTaxonomy ? { refreshTaxonomy: true } : {}) });
          if (!r2.ok) throw new Error(r2.error ?? "Publish failed");
          setBanner({ kind: "warn", text: `${r2.summary ?? "Published"} · ${imageCode === "missing_hero_image" ? "without a main image" : "without in-content images"}` });
          onAfterAction();
        } catch (e2) {
          setBanner({ kind: "error", text: (e2 as Error).message });
        }
        return;
      }
      /**
       * Broken internal links: the article would serve a 404 to readers. The
       * server already worked out what was probably meant, so the choice is
       * "go fix it" or "publish anyway" — and unlike the image case there is no
       * button that repairs it, so the honest default is to cancel and let the
       * operator edit the passage.
       */
      if (e instanceof ApiError && e.data?.code === "broken_internal_links") {
        const links = (e.data.brokenLinks as { url: string; anchor: string; suggestion: string | null }[] | undefined) ?? [];
        const lines = links
          .map((b) => `• "${b.anchor}" → ${b.url}${b.suggestion ? `\n     probably meant: ${b.suggestion}` : ""}`)
          .join("\n");
        const publishAnyway = window.confirm(
          `${links.length} internal link(s) point at a page that does not exist:\n\n${lines}\n\n` +
            `Publishing now serves a 404 to readers.\n\n` +
            `OK  → cancel and fix the link(s) in the Article tab (recommended)\n` +
            `Cancel → publish anyway with the broken link(s)`,
        );
        if (publishAnyway) return;
        try {
          const r2 = await api.publishToSanity(keywordId, { publish: true, allowBrokenLinks: true, ...(refreshTaxonomy ? { refreshTaxonomy: true } : {}) });
          if (!r2.ok) throw new Error(r2.error ?? "Publish failed");
          setBanner({ kind: "warn", text: `${r2.summary ?? "Published"} · with ${links.length} broken link(s)` });
          onAfterAction();
        } catch (e2) {
          setBanner({ kind: "error", text: (e2 as Error).message });
        }
        return;
      }
      /**
       * Duplicate gate at first publish — the same card Brief and Produce show,
       * naming the live article, with "publish anyway" as the explicit override.
       */
      if (e instanceof ApiError && e.status === 409 && e.data?.code === "duplicate_coverage") {
        setDuplicate({
          info: e.data.duplicate as DuplicateInfo,
          message: e.message,
          proceedLabel: "Publish anyway",
          retry: async () => {
            setDuplicate(null);
            try {
              const r2 = await api.publishToSanity(keywordId, { allowDuplicate: true, ...(refreshTaxonomy ? { refreshTaxonomy: true } : {}) });
              if (!r2.ok) throw new Error(r2.error ?? "Publish failed");
              setBanner({ kind: "warn", text: `${r2.summary ?? "Published"} · despite the live duplicate` });
              onAfterAction();
            } catch (e2) {
              setBanner({ kind: "error", text: (e2 as Error).message });
            }
          },
        });
        return;
      }
      // Another document owns this slug. No override: publishing would put two
      // posts at one URL. The message says what to do (refresh that post, or
      // change the slug in the Article tab).
      if (e instanceof ApiError && e.data?.code === "slug_taken") {
        setBanner({ kind: "error", text: e.message });
        return;
      }
      setBanner({ kind: "error", text: (e as Error).message });
    } finally {
      setPublishing(false);
    }
  };

  /**
   * Wrap an action call so success → banner OK + invalidate, failure → banner error.
   * Returns a `mutationFn`-compatible function for TanStack Query.
   */
  const mut = (fn: () => Promise<ActionResult>, label: string, mutationKey?: unknown[]) => ({
    ...(mutationKey ? { mutationKey } : {}),
    mutationFn: async () => {
      setBanner(null);
      const result = await fn();
      if (!result.ok) throw new Error(result.error ?? `${label} failed`);
      return result;
    },
    onSuccess: (r: ActionResult) => {
      setBanner({ kind: "ok" as const, text: r.summary ?? `${label} succeeded` });
      onAfterAction();
    },
    onError: (e: Error) => setBanner({ kind: "error" as const, text: e.message }),
  });

  const enrich = useMutation(mut(() => api.enrichKeyword(keywordId), "Enrich"));
  const enrichComp = useMutation(mut(() => api.enrichCompetitors(keywordId), "Enrich competitors"));
  const brief = useMutation({
    mutationFn: (opts: { allowDuplicate?: boolean } = {}) => api.generateBrief(keywordId, opts),
    onMutate: () => { setBanner(null); setDuplicate(null); },
    onSuccess: (r) => {
      // Returns as soon as the run STARTS; the banner and the article refresh
      // come from the status poll above, not from this response.
      setBanner({ kind: "ok", text: r.summary ?? "Brief started" });
      briefStatusQuery.refetch();
    },
    onError: (e: Error) => {
      if (e instanceof ApiError && e.status === 409 && e.data?.code === "duplicate_coverage") {
        setDuplicate({
          info: e.data.duplicate as DuplicateInfo,
          message: e.message,
          retry: () => brief.mutate({ allowDuplicate: true }),
        });
        return;
      }
      setBanner({ kind: "error", text: e.message });
    },
  });
  const briefRunning = briefJob?.status === "running" || brief.isPending;
  const refresh = useMutation(mut(() => api.refreshArticle(keywordId), "Refresh"));
  // Keyed so the Assets tab can show these as "in progress" via useIsMutating.
  const storyboard = useMutation(mut(() => api.generateStoryboard(keywordId), "Generate storyboard", ["gen", keywordId, "storyboard"]));
  const voiceover = useMutation(mut(() => api.generateVoiceover(keywordId, { mode: "article" }), "Generate voiceover", ["gen", keywordId, "voiceover"]));
  const viral = useMutation(mut(() => api.generateViral(keywordId), "Generate viral video", ["gen", keywordId, "viral"]));
  // Publish + image generation are plain async handlers (startPublish / startImages),
  // not mutations — the publish flow branches on the server's image-loss guard.

  const startProduce = (allowDuplicate = false) => {
    setBanner(null);
    setDuplicate(null);
    // GATE: if the brief names do-not-mention entities, make the operator confirm.
    // Context is shown so an ambiguous match (e.g. "כללית" = "general") can be judged.
    const dnm = detail.doNotMention ?? [];
    if (dnm.length > 0) {
      const list = dnm.map((v) => `  • ${v.name}${v.count > 1 ? ` (×${v.count})` : ""} — “…${v.context}…”`).join("\n");
      const ok = window.confirm(
        `⚠ This brief names ${dnm.length} do-not-mention entity(ies):\n\n${list}\n\n` +
        `Producing will carry them into the article. Best practice: regenerate or edit the brief to remove them first.\n\nProduce anyway?`,
      );
      if (!ok) return;
    }
    // Fire the produce (SSE just triggers + runs it; the bar is driven by the
    // polled server job, which survives refresh/reopen). onDone/onError refetch
    // fast when the stream is healthy; polling catches it otherwise.
    const unsub = api.produceArticle(
      keywordId,
      { withMeta: true, ...(allowDuplicate ? { allowDuplicate: true } : {}) },
      {
        onDone: () => { onAfterAction(); qc.invalidateQueries({ queryKey: ["produce-status", keywordId] }); },
        onDuplicate: (info, message) => {
          setDuplicate({ info, message, retry: () => startProduce(true) });
          qc.invalidateQueries({ queryKey: ["produce-status", keywordId] });
        },
        onError: (err: string) => { setBanner({ kind: "error", text: err }); qc.invalidateQueries({ queryKey: ["produce-status", keywordId] }); },
      },
    );
    // Start polling immediately so the bar appears right away.
    setTimeout(() => qc.invalidateQueries({ queryKey: ["produce-status", keywordId] }), 300);
    return unsub;
  };

  const produceInFlight = job?.status === "producing";
  const anyInFlight =
    enrich.isPending || enrichComp.isPending || briefRunning || refresh.isPending || produceInFlight
    || storyboard.isPending || voiceover.isPending || viral.isPending || publishing || imagesInFlight;

  return (
    <div className="border-b border-line bg-canvas p-5">
      <div className="flex flex-wrap gap-2">
        <ActionButton onClick={enrich.mutate as () => void} loading={enrich.isPending} disabled={anyInFlight}>
          Enrich
        </ActionButton>
        <ActionButton onClick={enrichComp.mutate as () => void} loading={enrichComp.isPending} disabled={anyInFlight || !k.serp}>
          Scrape competitors
        </ActionButton>
        <ActionButton
          onClick={() => brief.mutate({})}
          loading={briefRunning}
          disabled={anyInFlight || detail.extracts.length === 0 || !!detail.primary}
          title={detail.primary ? `Briefed via primary "${detail.primary.keyword}"` : undefined}
        >
          {briefRunning ? "Generating brief…" : detail.primary ? "Brief (via primary)" : "Generate brief"}
        </ActionButton>
        <ActionButton
          onClick={() => startProduce()}
          loading={produceInFlight}
          disabled={anyInFlight || !k.brief || !!detail.primary}
          title={detail.primary ? `Produced via primary "${detail.primary.keyword}"` : undefined}
        >
          {detail.primary ? "Produce (via primary)" : "Produce article"}
        </ActionButton>
        <ActionButton onClick={refresh.mutate as () => void} loading={refresh.isPending} disabled={anyInFlight || !k.article}>
          Refresh
        </ActionButton>
        <DistributeMenu keywordId={keywordId} disabled={anyInFlight || !k.article} onResult={(r) => {
          setBanner(
            r.ok
              ? {
                  kind: "ok",
                  text: r.summary ?? "Distributed",
                  ...(typeof r.viewUrl === "string" ? { link: { label: "Open in Postiz ↗", url: r.viewUrl } } : {}),
                }
              : { kind: "error", text: r.error ?? "Distribution failed" },
          );
          if (r.ok) onAfterAction();
        }} />
      </div>

      {/* Post-production actions: images, voiceover, storyboard/viral, publish. Disabled until an article exists. */}
      <div className="mt-2 flex flex-wrap gap-2">
        <ActionButton
          onClick={startImages}
          loading={imagesInFlight}
          disabled={anyInFlight || !k.article}
          title={!k.article ? "Produce an article first" : "Generate the missing in-content, hero + social images (skips any already made)"}
        >
          {imagesInFlight ? "Generating images…" : "Generate images"}
        </ActionButton>
        <ActionButton
          onClick={voiceover.mutate as () => void}
          loading={voiceover.isPending}
          disabled={anyInFlight || !k.article}
          title={!k.article ? "Produce an article first" : "ElevenLabs narrates a 90-120s script over the article"}
        >
          Voiceover
        </ActionButton>
        <ActionButton
          onClick={storyboard.mutate as () => void}
          loading={storyboard.isPending}
          disabled={anyInFlight || !k.article}
          title={!k.article ? "Produce an article first" : "Sonnet → JSON storyboard (5-10 scenes)"}
        >
          {k.storyboard ? "Storyboard ✓" : "Storyboard"}
        </ActionButton>
        <ActionButton
          onClick={viral.mutate as () => void}
          loading={viral.isPending}
          disabled={anyInFlight || !k.storyboard}
          title={!k.storyboard ? "Generate a storyboard first" : "Render the viral 9:16 MP4 (~$1.10, ~5 min)"}
        >
          Viral video
        </ActionButton>
        <ActionButton
          onClick={runReview}
          loading={reviewing}
          disabled={anyInFlight || !k.article}
          title={!k.article ? "Produce an article first" : "LLM fact-check: wrong terms, unsourced stats, medical overreach, off-topic (~$0.05)"}
        >
          Review accuracy
        </ActionButton>
        <ActionButton
          onClick={startPublish}
          loading={publishing}
          disabled={anyInFlight || !k.article}
          title={
            detail.live?.isLive
              ? `Already live${detail.live.url ? ` at ${detail.live.url}` : ""} — re-publishing updates it in place`
              : !k.article
                ? "Produce an article first"
                : "Markdown → Portable Text → blog-posts draft. Warns if in-content images aren't generated yet."
          }
        >
          {detail.live?.isLive ? "Re-publish (live)" : "Publish to Sanity"}
        </ActionButton>
        <ActionButton
          onClick={() => setRegenOpen(true)}
          loading={false}
          disabled={anyInFlight || !k.article || !(k.status === "published" || k.status === "distributed" || !!k.firstPublishedAt)}
          title={
            !k.article
              ? "Produce an article first"
              : !(k.status === "published" || k.status === "distributed" || !!k.firstPublishedAt)
              ? "Publish the article first — regenerate is for live pieces that dropped in ranking or need updating"
              : "Re-run the pipeline (SERP → meta → body → images → re-publish), preserving the original publish date"
          }
        >
          Regenerate
        </ActionButton>
      </div>

      {regenOpen && (
        <RegenerateModal
          keywordId={keywordId}
          detail={detail}
          onClose={() => setRegenOpen(false)}
          onAfterAction={onAfterAction}
        />
      )}

      {duplicate && (
        <DuplicateBlock
          info={duplicate.info}
          message={duplicate.message}
          onProceed={() => { const go = duplicate.retry; setDuplicate(null); go(); }}
          proceedLabel={duplicate.proceedLabel}
          onMerge={
            detail.keyword.articleMarkdown && duplicate.info.sanityId && duplicate.info.kind === "article"
              ? () => mergeIntoLive(duplicate.info)
              : undefined
          }
          merging={merging}
          onDismiss={() => setDuplicate(null)}
        />
      )}

      {banner && (
        <div className={`mt-3 flex items-center gap-2 rounded-md px-3 py-2 text-xs ${
          banner.kind === "ok" ? "bg-accent/10 text-accent" :
          banner.kind === "warn" ? "bg-warn/10 text-warn" :
          "bg-danger/10 text-danger"
        }`}>
          <span>{banner.text}</span>
          {banner.link && (
            <a
              href={banner.link.url}
              target="_blank"
              rel="noreferrer"
              className="ml-auto shrink-0 font-medium underline hover:no-underline"
            >
              {banner.link.label}
            </a>
          )}
        </div>
      )}

      {job && job.status !== "idle" && (
        <ProduceProgress job={job} />
      )}

      {review && (
        <div className="mt-3 rounded-md border border-line bg-white p-3">
          <div className="mb-2 text-xs font-medium" dir="auto">
            Accuracy review {review.findings.length === 0 ? "— clean ✓" : `— ${review.findings.length} findings`}
            {review.summary && <span className="ml-1 font-normal text-muted">· {review.summary}</span>}
          </div>
          <ul className="space-y-1.5">
            {review.findings.map((f, i) => (
              <li key={i} className="rounded border border-line/60 p-2 text-xs" dir="auto">
                <div className="flex items-center gap-2">
                  <span className={`pill ${f.severity === "high" ? "pill-danger" : f.severity === "medium" ? "pill-warn" : "pill-muted"}`}>{f.severity}</span>
                  <span className="font-mono text-2xs text-muted">{f.type}</span>
                </div>
                <div className="mt-1 text-muted">"{f.excerpt}"</div>
                <div className="mt-0.5">{f.issue}</div>
                <div className="mt-0.5 text-accent">→ {f.suggestion}</div>
              </li>
            ))}
          </ul>
          <div className="mt-2 text-2xs text-muted">High/medium findings are also logged to the Errors panel.</div>
        </div>
      )}
    </div>
  );
}

/**
 * The stop sign in front of a second article on a subject we have already
 * written about.
 *
 * Everything here exists because of what the old advisory card could not do:
 * it sat at the bottom of a tab, said "Partial · 79% — Safe to produce", and
 * the article got written anyway. So this appears where the buttons are, states
 * the collision in a sentence, and makes the live page a link the operator can
 * actually open. "Produce anyway" stays — sometimes a second piece is right —
 * but it is now a decision taken with the existing article on screen.
 */
function DuplicateBlock({
  info, message, onProceed, onDismiss, proceedLabel = "Produce anyway", onMerge, merging = false,
}: {
  info: DuplicateInfo;
  message: string;
  onProceed: () => void;
  proceedLabel?: string;
  onDismiss: () => void;
  /** Offered when this keyword already has a produced article and the rival is a live blog article. */
  onMerge?: () => void;
  merging?: boolean;
}) {
  return (
    <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-amber-900">
            We have already written about this
          </div>
          <p className="mt-1 text-xs leading-relaxed text-amber-900/90">{message}</p>

          {info.url && (
            <a
              href={info.url}
              target="_blank"
              rel="noreferrer"
              className="mt-2 flex items-center gap-2 rounded-md border border-amber-300 bg-white px-2.5 py-1.5 hover:bg-amber-50"
            >
              <FileText className="h-3.5 w-3.5 shrink-0 text-amber-700" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-xs font-medium text-ink" dir="auto" title={info.title ?? ""}>
                  {info.title ?? info.url}
                </span>
                <span className="text-[10px] text-muted">
                  {info.gsc
                    ? `Search Console: ranks #${info.gsc.position.toFixed(1)} for "${info.gsc.query}"`
                    : info.basis === "operator-keyword"
                    ? "Its operator-set keyword is this exact phrase"
                    : "Its title already targets this phrase"}
                </span>
              </span>
              <ExternalLink className="h-3 w-3 shrink-0 text-amber-700" />
            </a>
          )}

          <div className="mt-2.5 flex items-center gap-2">
            <button
              type="button"
              onClick={onDismiss}
              className="rounded-md bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700"
            >
              Keep the existing article
            </button>
            {onMerge && (
              <button
                type="button"
                onClick={onMerge}
                disabled={merging}
                title="Publish this article INTO the live one: same URL, original publish date, Studio fields kept. Nothing is published by this button."
                className="rounded-md border border-amber-600 bg-white px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
              >
                {merging ? "Linking…" : "Merge into this article"}
              </button>
            )}
            <button
              type="button"
              onClick={onProceed}
              className="rounded-md border border-amber-300 px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
            >
              {proceedLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionButton({
  onClick,
  loading,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      title={title}
      className={`btn ${loading ? "btn-ghost" : "btn-primary"} text-sm`}
    >
      {loading ? "Working…" : children}
    </button>
  );
}

function ProduceProgress({ job }: { job: ProduceJobView }) {
  const total = job.total ?? 0;
  const done = job.done ?? 0;
  const pct = total > 0 ? Math.min(100, (done / total) * 100) : 0;
  const elapsed = job.startedAt ? Math.round((Date.now() - new Date(job.startedAt).getTime()) / 1000) : null;
  const label = job.status === "done" ? "Production complete" : job.status === "error" ? "Production failed" : "Producing…";
  const barColor = job.status === "error" ? "bg-danger" : job.status === "done" ? "bg-accent" : "bg-brand";
  return (
    <div className="mt-3 rounded-md border border-line bg-white p-3">
      <div className="mb-2 flex items-center justify-between text-xs">
        <span className="font-medium">{label}</span>
        <span className="text-muted">{done} / {total || "?"} sections{elapsed != null && job.status === "producing" ? ` · ${elapsed}s` : ""}</span>
      </div>
      <div className="mb-2 h-1.5 overflow-hidden rounded-full bg-line">
        <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${job.status === "done" ? 100 : pct}%` }} />
      </div>
      {job.status === "producing" && (
        <div className="text-2xs text-muted" dir="auto">
          {job.currentHeading ? `Writing: ${job.currentHeading}` : "Starting…"}
          <span className="ml-1">· keeps running if you close this — reopen to check progress.</span>
        </div>
      )}
      {job.status === "done" && job.summary && (
        <div className="border-t border-line pt-2 font-mono text-[11px] text-muted">
          {job.summary.totalWordCount ?? "?"}w · ${job.summary.estimatedCostUsd?.toFixed(4) ?? "?"} · model {job.summary.modelUsed ?? "?"}
        </div>
      )}
      {job.status === "error" && job.error && (
        <div className="text-2xs text-danger">{job.error}</div>
      )}
    </div>
  );
}

function DistributeMenu({
  keywordId,
  disabled,
  onResult,
}: {
  keywordId: string;
  disabled: boolean;
  onResult: (r: ActionResult) => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const fireTelegram = async () => {
    setBusy(true);
    setOpen(false);
    const url = prompt("Public article URL?");
    if (!url) { setBusy(false); return; }
    try {
      const r = await api.distribute(keywordId, "telegram", { url });
      onResult(r);
    } catch (e) {
      onResult({ ok: false, error: (e as Error).message });
    } finally { setBusy(false); }
  };

  const fireBrevoTest = async () => {
    setBusy(true);
    setOpen(false);
    const url = prompt("Public article URL?");
    if (!url) { setBusy(false); return; }
    const testTo = prompt("Test recipient email?", "info@alternabe.co.il");
    if (!testTo) { setBusy(false); return; }
    try {
      const r = await api.distribute(keywordId, "brevo", { url, testTo });
      onResult(r);
    } catch (e) {
      onResult({ ok: false, error: (e as Error).message });
    } finally { setBusy(false); }
  };

  const fireYoutubeDryRun = async () => {
    setBusy(true);
    setOpen(false);
    const url = prompt("Public article URL? (used as CTA in description)", "https://alternabe.co.il/");
    if (!url) { setBusy(false); return; }
    try {
      const r = await api.distribute(keywordId, "youtube", { url, dryRun: true });
      onResult(r);
    } catch (e) {
      onResult({ ok: false, error: (e as Error).message });
    } finally { setBusy(false); }
  };

  const fireYoutubeUpload = async () => {
    setBusy(true);
    setOpen(false);
    const url = prompt("Public article URL? (used as CTA in description)", "https://alternabe.co.il/");
    if (!url) { setBusy(false); return; }
    const ok = confirm(
      "Upload to YouTube as PRIVATE? (You can change privacy on YouTube Studio after review.)",
    );
    if (!ok) { setBusy(false); return; }
    try {
      const r = await api.distribute(keywordId, "youtube", { url, privacyStatus: "private" });
      onResult(r);
    } catch (e) {
      onResult({ ok: false, error: (e as Error).message });
    } finally { setBusy(false); }
  };

  const fireFacebook = async (dryRun: boolean) => {
    setBusy(true);
    setOpen(false);
    const url = prompt("Public article URL?", "https://alternabe.co.il/");
    if (!url) { setBusy(false); return; }
    try {
      const r = await api.distribute(keywordId, "facebook", { url, dryRun });
      onResult(r);
    } catch (e) {
      onResult({ ok: false, error: (e as Error).message });
    } finally { setBusy(false); }
  };

  const fireInstagram = async (dryRun: boolean) => {
    setBusy(true);
    setOpen(false);
    const url = prompt("Public article URL?", "https://alternabe.co.il/");
    if (!url) { setBusy(false); return; }
    let imageUrl: string | undefined;
    if (!dryRun) {
      const ans = prompt(
        "IG requires a PUBLIC image URL (it can't fetch from your laptop).\nPaste the Sanity / CDN URL of the hero image:",
        "",
      );
      if (!ans) { setBusy(false); return; }
      imageUrl = ans;
    }
    try {
      const r = await api.distribute(keywordId, "instagram", { url, imageUrl, dryRun });
      onResult(r);
    } catch (e) {
      onResult({ ok: false, error: (e as Error).message });
    } finally { setBusy(false); }
  };

  const fireTiktok = async (privacyLevel: "SELF_ONLY" | "MUTUAL_FOLLOW_FRIENDS" | "PUBLIC_TO_EVERYONE", dryRun: boolean) => {
    setBusy(true);
    setOpen(false);
    const url = prompt("Public article URL?", "https://alternabe.co.il/");
    if (!url) { setBusy(false); return; }
    if (!dryRun) {
      const label = privacyLevel === "SELF_ONLY"
        ? "private (visible only to your account)"
        : privacyLevel === "MUTUAL_FOLLOW_FRIENDS"
        ? "to mutual-follow friends"
        : "PUBLIC (visible to everyone)";
      const ok = confirm(`Post to TikTok as ${label}? Requires the short.mp4 asset to exist.`);
      if (!ok) { setBusy(false); return; }
    }
    try {
      const r = await api.distribute(keywordId, "tiktok", { url, privacyLevel, dryRun });
      onResult(r);
    } catch (e) {
      onResult({ ok: false, error: (e as Error).message });
    } finally { setBusy(false); }
  };

  const fireX = async (xMode: "thread" | "single", dryRun: boolean) => {
    setBusy(true);
    setOpen(false);
    const url = prompt("Public article URL?", "https://alternabe.co.il/");
    if (!url) { setBusy(false); return; }
    if (!dryRun) {
      const ok = confirm(`Post to X as a ${xMode === "thread" ? "multi-tweet thread" : "single tweet"}? Tweets are public + permanent.`);
      if (!ok) { setBusy(false); return; }
    }
    try {
      const r = await api.distribute(keywordId, "x", { url, xMode, dryRun });
      onResult(r);
    } catch (e) {
      onResult({ ok: false, error: (e as Error).message });
    } finally { setBusy(false); }
  };

  const fireWhatsapp = async (mode: "freeform" | "template", community: "clients" | "therapists", dryRun: boolean) => {
    setBusy(true);
    setOpen(false);
    const url = prompt("Public article URL?", "https://alternabe.co.il/");
    if (!url) { setBusy(false); return; }
    if (!dryRun && mode === "template") {
      const ok = confirm("Send approved-template WhatsApp message? This uses the template configured in Credentials (WHATSAPP_ANNOUNCEMENT_TEMPLATE).");
      if (!ok) { setBusy(false); return; }
    }
    if (!dryRun && mode === "freeform") {
      const ok = confirm("Send free-form WhatsApp message? Only deliverable to recipients who messaged you in the last 24h.");
      if (!ok) { setBusy(false); return; }
    }
    try {
      const r = await api.distribute(keywordId, "whatsapp", { url, messageType: mode, community, dryRun });
      onResult(r);
    } catch (e) {
      onResult({ ok: false, error: (e as Error).message });
    } finally { setBusy(false); }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={disabled || busy}
        className="btn-primary text-sm"
      >
        {busy ? "Sending…" : "Distribute ▾"}
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 w-64 overflow-hidden rounded-md border border-line bg-white shadow-lg">
          {/* "Send to Postiz" lived here. It sent ONE generic caption to every
              channel at once with no per-platform settings and no attribution,
              and it is retired (the route answers 410). The Social tab replaces
              it: a caption per platform AND per format, checked, dripped over
              two weeks. Left as a signpost rather than removed silently — an
              operator who used this daily needs to be told where it went. */}
          <div className="px-3 py-2 text-xs text-muted">
            שליחה לסושיאל עברה לטאב <span className="font-medium">Social</span> — כיתוב לכל פלטפורמה, לא אחד לכולן.
          </div>
          <div className="border-t border-line" />
          <button onClick={fireTelegram} className="block w-full px-3 py-2 text-left text-sm hover:bg-canvas">
            Telegram channel
          </button>
          <button onClick={fireBrevoTest} className="block w-full px-3 py-2 text-left text-sm hover:bg-canvas">
            Brevo (test email)
          </button>
          <div className="border-t border-line" />
          <button onClick={fireYoutubeDryRun} className="block w-full px-3 py-2 text-left text-sm hover:bg-canvas">
            YouTube — dry run <span className="text-xs text-muted">(preview metadata)</span>
          </button>
          <button onClick={fireYoutubeUpload} className="block w-full px-3 py-2 text-left text-sm hover:bg-canvas">
            YouTube — upload <span className="text-xs text-muted">(private)</span>
          </button>
          <div className="border-t border-line" />
          <button onClick={() => fireFacebook(true)} className="block w-full px-3 py-2 text-left text-sm hover:bg-canvas">
            Facebook — dry run <span className="text-xs text-muted">(preview caption)</span>
          </button>
          <button onClick={() => fireFacebook(false)} className="block w-full px-3 py-2 text-left text-sm hover:bg-canvas">
            Facebook — post <span className="text-xs text-muted">(photo + caption)</span>
          </button>
          <div className="border-t border-line" />
          <button onClick={() => fireInstagram(true)} className="block w-full px-3 py-2 text-left text-sm hover:bg-canvas">
            Instagram — dry run <span className="text-xs text-muted">(preview)</span>
          </button>
          <button onClick={() => fireInstagram(false)} className="block w-full px-3 py-2 text-left text-sm hover:bg-canvas">
            Instagram — post <span className="text-xs text-muted">(needs public image URL)</span>
          </button>
          <div className="border-t border-line" />
          <button onClick={() => fireWhatsapp("freeform", "clients", true)} className="block w-full px-3 py-2 text-left text-sm hover:bg-canvas">
            WhatsApp — dry run <span className="text-xs text-muted">(preview body)</span>
          </button>
          <button onClick={() => fireWhatsapp("freeform", "clients", false)} className="block w-full px-3 py-2 text-left text-sm hover:bg-canvas">
            WhatsApp — free-form (clients) <span className="text-xs text-muted">(24h window only)</span>
          </button>
          <button onClick={() => fireWhatsapp("template", "clients", false)} className="block w-full px-3 py-2 text-left text-sm hover:bg-canvas">
            WhatsApp — template (clients) <span className="text-xs text-muted">(broadcast-safe)</span>
          </button>
          <button onClick={() => fireWhatsapp("template", "therapists", false)} className="block w-full px-3 py-2 text-left text-sm hover:bg-canvas">
            WhatsApp — template (therapists) <span className="text-xs text-muted">(broadcast-safe)</span>
          </button>
          <div className="border-t border-line" />
          <button onClick={() => fireX("thread", true)} className="block w-full px-3 py-2 text-left text-sm hover:bg-canvas">
            X — dry run (thread) <span className="text-xs text-muted">(preview tweets)</span>
          </button>
          <button onClick={() => fireX("thread", false)} className="block w-full px-3 py-2 text-left text-sm hover:bg-canvas">
            X — post thread <span className="text-xs text-muted">(H1 + body tweets + URL)</span>
          </button>
          <button onClick={() => fireX("single", false)} className="block w-full px-3 py-2 text-left text-sm hover:bg-canvas">
            X — single post <span className="text-xs text-muted">(H1 + URL only)</span>
          </button>
          <div className="border-t border-line" />
          <button onClick={() => fireTiktok("SELF_ONLY", true)} className="block w-full px-3 py-2 text-left text-sm hover:bg-canvas">
            TikTok — dry run <span className="text-xs text-muted">(preview caption + asset)</span>
          </button>
          <button onClick={() => fireTiktok("SELF_ONLY", false)} className="block w-full px-3 py-2 text-left text-sm hover:bg-canvas">
            TikTok — post (SELF_ONLY) <span className="text-xs text-muted">(sandbox-safe)</span>
          </button>
          <button onClick={() => fireTiktok("MUTUAL_FOLLOW_FRIENDS", false)} className="block w-full px-3 py-2 text-left text-sm hover:bg-canvas">
            TikTok — post (Mutual follows) <span className="text-xs text-muted">(requires app review)</span>
          </button>
          <button onClick={() => fireTiktok("PUBLIC_TO_EVERYONE", false)} className="block w-full px-3 py-2 text-left text-sm hover:bg-canvas">
            TikTok — post PUBLIC <span className="text-xs text-muted">(requires app review)</span>
          </button>
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Tabs
// ───────────────────────────────────────────────────────────────────────────

function OverviewTab({ detail, coverage, loadingCoverage }: { detail: KeywordDetail; coverage?: CoverageResult; loadingCoverage?: boolean }) {
  const k = detail.keyword;
  return (
    <div className="space-y-5">
      {/* Coverage — what existing live page (if any) already covers this keyword. */}
      <CoverageCard coverage={coverage} loading={loadingCoverage} />

      {/* Primary / secondary banner — surfaces cannibalization grouping prominently. */}
      {detail.primary && (
        <div className="rounded-xl border border-info/30 bg-info-soft p-4">
          <div className="text-2xs font-semibold uppercase tracking-wider text-blue-700">
            Secondary keyword
          </div>
          <p className="mt-1.5 text-sm text-ink">
            This keyword is folded into the article for{" "}
            <a href={`#/pipeline?open=${encodeURIComponent(detail.primary.id)}`} className="font-semibold text-blue-700 hover:underline" dir="auto">
              {detail.primary.keyword}
            </a>
            . The brief generator will weave it into the primary's semantic must-cover list — don't produce a separate article for it.
          </p>
          <div className="mt-1.5 text-2xs text-muted">
            Primary status: {detail.primary.status}
            {detail.primary.cluster && ` · cluster: ${detail.primary.cluster}`}
          </div>
        </div>
      )}
      {detail.secondaries.length > 0 && (
        <div className="rounded-xl border border-brand-200 bg-brand-50 p-4">
          <div className="text-2xs font-semibold uppercase tracking-wider text-brand-700">
            Primary with {detail.secondaries.length} secondary keyword{detail.secondaries.length === 1 ? "" : "s"}
          </div>
          <p className="mt-1.5 text-sm text-ink">
            The article for this keyword should also cover these folded-in terms. The brief generator will pick them up automatically.
          </p>
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {detail.secondaries.map((s) => (
              <li key={s.id}>
                <a
                  href={`#/pipeline?open=${encodeURIComponent(s.id)}`}
                  className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-2xs ring-1 ring-brand/20 hover:ring-brand"
                  dir="auto"
                >
                  {s.keyword}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <EditableFields detail={detail} />

      {k.importedMeta && Object.keys(k.importedMeta).length > 0 && (
        <details className="rounded-lg border border-line bg-canvas-deep/40 p-3">
          <summary className="cursor-pointer text-2xs font-semibold uppercase tracking-wider text-muted">
            Imported CSV metadata ({Object.keys(k.importedMeta).length} extra column{Object.keys(k.importedMeta).length === 1 ? "" : "s"})
          </summary>
          <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            {Object.entries(k.importedMeta).map(([key, value]) => (
              <div key={key} className="contents">
                <dt className="truncate font-medium text-muted">{key}</dt>
                <dd className="truncate" dir="auto">{String(value)}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}

      <DangerZone detail={detail} />
    </div>
  );
}

function EditableFields({ detail }: { detail: KeywordDetail }) {
  const qc = useQueryClient();
  const k = detail.keyword;
  const clustersQuery = useQuery({ queryKey: ["pipeline", "clusters"], queryFn: () => api.listClusters() });
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState(k.notes ?? "");
  const [editingRejection, setEditingRejection] = useState(false);
  const [rejectionDraft, setRejectionDraft] = useState(k.rejectionReason ?? "");
  const [editingSecKws, setEditingSecKws] = useState(false);
  const [secKws, setSecKws] = useState<string[]>(k.manualSecondaryKeywords ?? []);
  const [newSecKw, setNewSecKw] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aiNote, setAiNote] = useState<string | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["pipeline"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const aiAssignCluster = async () => {
    setBusy("cluster");
    setError(null);
    setAiNote(null);
    try {
      const r = await api.aiAssignClusters([k.id]);
      setAiNote(summariseAiAssign(r.results));
      invalidate();
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(null); }
  };

  const patch = async (body: Parameters<typeof api.patchKeyword>[1], tag: string) => {
    setBusy(tag);
    setError(null);
    try {
      await api.patchKeyword(k.id, body);
      invalidate();
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(null); }
  };

  const clusters = clustersQuery.data?.clusters ?? [];
  const currentClusterId = k.clusterId
    ?? clusters.find((c) => c.nameEn === k.cluster)?.id
    ?? "";

  return (
    <>
      {error && (
        <div className="rounded-md bg-danger-soft px-3 py-2 text-xs text-danger">{error}</div>
      )}

      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <Field label="Language">{k.language}</Field>

        {/* Priority — inline editor */}
        <div>
          <dt className="text-[10px] uppercase tracking-wide text-muted">Priority</dt>
          <dd className="mt-0.5">
            <select
              value={k.priority}
              onChange={(e) => patch({ priority: e.target.value as Priority }, "priority")}
              disabled={busy !== null}
              className={`h-6 rounded-full border-0 px-2 text-2xs font-semibold uppercase tracking-wider focus:ring-1 ${
                k.priority === "urgent" ? "bg-danger/10 text-danger" :
                k.priority === "high"   ? "bg-warn/10 text-warn" :
                k.priority === "medium" ? "bg-brand/10 text-brand" :
                                          "bg-line text-muted"
              }`}
            >
              <option value="urgent">urgent</option>
              <option value="high">high</option>
              <option value="medium">medium</option>
              <option value="low">low</option>
            </select>
          </dd>
        </div>

        {/* Status — inline editor */}
        <div>
          <dt className="text-[10px] uppercase tracking-wide text-muted">Status</dt>
          <dd className="mt-0.5">
            <select
              value={k.status}
              onChange={(e) => patch({ status: e.target.value as Status }, "status")}
              disabled={busy !== null}
              className="h-6 rounded border border-line bg-white px-1.5 text-2xs"
            >
              <option value="backlog">backlog</option>
              <option value="researching">researching</option>
              <option value="briefed">briefed</option>
              <option value="drafting">drafting</option>
              <option value="reviewing">reviewing</option>
              <option value="published">published</option>
              <option value="distributed">distributed</option>
              <option value="archived">archived</option>
              <option value="rejected">rejected</option>
            </select>
          </dd>
        </div>

        <Field label="Intent">{k.targetIntent ?? "—"}</Field>

        {/* Cluster — inline editor */}
        <div>
          <dt className="text-[10px] uppercase tracking-wide text-muted">Cluster</dt>
          <dd className="mt-0.5">
            <ClusterPicker
              value={currentClusterId || null}
              clusters={clusters}
              onChange={(id) => patch({ clusterId: id }, "cluster")}
              onAiAssign={aiAssignCluster}
              disabled={busy !== null || clustersQuery.isPending}
              busy={busy === "cluster"}
              placeholder="(none)"
            />
            {aiNote && <div className="mt-1 text-2xs text-muted" dir="auto">{aiNote}</div>}
          </dd>
        </div>

        <Field label="Pillar?">{k.pillarPage ? "yes" : "no"}</Field>
        <Field label="Created">{new Date(k.createdAt).toLocaleString("en-GB")}</Field>
        <Field label="Updated">{new Date(k.updatedAt).toLocaleString("en-GB")}</Field>
        <Field label="Last researched">{k.lastResearchedAt ? new Date(k.lastResearchedAt).toLocaleString("en-GB") : "—"}</Field>
        <Field label="Last reviewed">{k.lastReviewedAt ? new Date(k.lastReviewedAt).toLocaleString("en-GB") : "—"}</Field>
        <Field label="Author">{k.authorId ?? "—"}</Field>
        <Field label="Source tags" wide>
          {k.sourceTags.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {k.sourceTags.map((t) => (
                <span key={t} className="rounded-full bg-line-soft px-1.5 py-0.5 text-2xs font-mono text-muted-dark">{t}</span>
              ))}
            </div>
          ) : "—"}
        </Field>

        {/* Secondary keywords — operator-set, fold into link-matching */}
        <div className="col-span-2">
          <div className="flex items-baseline justify-between">
            <dt className="text-[10px] uppercase tracking-wide text-muted" title="Operator-set secondary keywords — folded into internal-link matching">Secondary keywords</dt>
            {!editingSecKws && (
              <button onClick={() => { setSecKws(k.manualSecondaryKeywords ?? []); setEditingSecKws(true); }} className="text-2xs text-brand-700 hover:underline">
                {(k.manualSecondaryKeywords?.length ?? 0) > 0 ? "Edit" : "Add"}
              </button>
            )}
          </div>
          <dd className="mt-1">
            {editingSecKws ? (
              <div className="space-y-1.5">
                {secKws.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {secKws.map((s, i) => (
                      <span key={i} className="inline-flex items-center gap-0.5 rounded-full bg-line-soft px-1.5 py-0.5 text-2xs text-muted-dark" dir="auto">
                        {s}
                        <button onClick={() => setSecKws((p) => p.filter((x) => x !== s))} className="text-muted hover:text-danger" title="Remove">×</button>
                      </span>
                    ))}
                  </div>
                )}
                <input
                  value={newSecKw}
                  onChange={(e) => setNewSecKw(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const v = newSecKw.trim();
                      if (v && !secKws.some((x) => x.toLowerCase() === v.toLowerCase())) setSecKws((p) => [...p, v]);
                      setNewSecKw("");
                    }
                  }}
                  placeholder="Add secondary keyword + Enter"
                  className="input w-full text-xs"
                  dir="auto"
                />
                {(() => {
                  const sugg = detail.secondaries
                    .map((s) => s.keyword)
                    .filter((kw) => kw.toLowerCase() !== k.keyword.toLowerCase() && !secKws.some((x) => x.toLowerCase() === kw.toLowerCase()))
                    .slice(0, 8);
                  return sugg.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      <span className="text-2xs text-muted">From related:</span>
                      {sugg.map((kw, i) => (
                        <button key={i} onClick={() => setSecKws((p) => [...p, kw])} className="rounded-full border border-dashed border-line px-1.5 py-0.5 text-2xs text-muted hover:bg-line" dir="auto" title="Add as secondary keyword">+ {kw}</button>
                      ))}
                    </div>
                  ) : null;
                })()}
                <div className="flex gap-1">
                  <button
                    onClick={async () => { await patch({ manualSecondaryKeywords: secKws }, "secKws"); setEditingSecKws(false); }}
                    disabled={busy !== null}
                    className="btn btn-primary text-xs"
                  >
                    Save
                  </button>
                  <button onClick={() => setEditingSecKws(false)} className="btn btn-ghost text-xs">Cancel</button>
                </div>
              </div>
            ) : (
              (k.manualSecondaryKeywords?.length ?? 0) > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {k.manualSecondaryKeywords.map((s, i) => (
                    <span key={i} className="rounded-full bg-line-soft px-1.5 py-0.5 text-2xs text-muted-dark" dir="auto">{s}</span>
                  ))}
                </div>
              ) : <span className="text-xs text-muted">—</span>
            )}
          </dd>
        </div>

        {/* Notes — inline editor */}
        <div className="col-span-2">
          <div className="flex items-baseline justify-between">
            <dt className="text-[10px] uppercase tracking-wide text-muted">Notes</dt>
            {!editingNotes && (
              <button onClick={() => { setNotesDraft(k.notes ?? ""); setEditingNotes(true); }} className="text-2xs text-brand-700 hover:underline">
                {k.notes ? "Edit" : "Add"}
              </button>
            )}
          </div>
          <dd className="mt-1">
            {editingNotes ? (
              <div className="space-y-1.5">
                <textarea
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  rows={3}
                  className="input w-full text-xs"
                  dir="auto"
                  autoFocus
                />
                <div className="flex gap-1">
                  <button
                    onClick={async () => {
                      await patch({ notes: notesDraft.trim() || null }, "notes");
                      setEditingNotes(false);
                    }}
                    disabled={busy !== null}
                    className="btn btn-primary text-xs"
                  >
                    Save
                  </button>
                  <button onClick={() => setEditingNotes(false)} className="btn btn-ghost text-xs">Cancel</button>
                </div>
              </div>
            ) : (
              k.notes ? <pre className="whitespace-pre-wrap text-xs">{k.notes}</pre> : <span className="text-xs text-muted">—</span>
            )}
          </dd>
        </div>

        {/* Rejection reason — only when status is rejected */}
        {k.status === "rejected" && (
          <div className="col-span-2">
            <div className="flex items-baseline justify-between">
              <dt className="text-[10px] uppercase tracking-wide text-muted">Rejection reason</dt>
              {!editingRejection && (
                <button onClick={() => { setRejectionDraft(k.rejectionReason ?? ""); setEditingRejection(true); }} className="text-2xs text-brand-700 hover:underline">
                  {k.rejectionReason ? "Edit" : "Add"}
                </button>
              )}
            </div>
            <dd className="mt-1">
              {editingRejection ? (
                <div className="space-y-1.5">
                  <textarea
                    value={rejectionDraft}
                    onChange={(e) => setRejectionDraft(e.target.value)}
                    rows={2}
                    className="input w-full text-xs"
                    dir="auto"
                    autoFocus
                  />
                  <div className="flex gap-1">
                    <button
                      onClick={async () => {
                        await patch({ rejectionReason: rejectionDraft.trim() || null }, "rejection");
                        setEditingRejection(false);
                      }}
                      disabled={busy !== null}
                      className="btn btn-primary text-xs"
                    >
                      Save
                    </button>
                    <button onClick={() => setEditingRejection(false)} className="btn btn-ghost text-xs">Cancel</button>
                  </div>
                </div>
              ) : (
                k.rejectionReason
                  ? <pre className="whitespace-pre-wrap text-xs text-danger">{k.rejectionReason}</pre>
                  : <span className="text-xs text-muted italic">(none — click Add)</span>
              )}
            </dd>
          </div>
        )}
      </dl>
    </>
  );
}

function DangerZone({ detail }: { detail: KeywordDetail }) {
  const qc = useQueryClient();
  const k = detail.keyword;
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["pipeline"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const archive = async () => {
    if (!confirm(`Archive "${k.keyword}"? It'll disappear from Tasks + Pipeline but stay in the DB.`)) return;
    setBusy("archive");
    setError(null);
    try {
      await api.patchKeyword(k.id, { status: "archived" });
      invalidate();
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(null); }
  };

  const reject = async () => {
    const reason = prompt(`Reject "${k.keyword}"? Optional reason:`, k.rejectionReason ?? "");
    if (reason === null) return; // operator cancelled
    setBusy("reject");
    setError(null);
    try {
      await api.patchKeyword(k.id, { status: "rejected", rejectionReason: reason.trim() || null });
      invalidate();
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(null); }
  };

  const hardDelete = async () => {
    if (!confirm(`PERMANENTLY DELETE "${k.keyword}"? Cascades to extracts/distributions. NOT reversible.`)) return;
    setBusy("delete");
    setError(null);
    try {
      await api.deleteKeyword(k.id);
      // Close the panel since the entity is gone.
      window.location.hash = "/pipeline";
      invalidate();
    } catch (e) {
      setError((e as Error).message);
      setBusy(null);
    }
  };

  return (
    <div className="rounded-xl border border-danger/20 bg-danger-soft/40 p-4">
      <div className="text-2xs font-semibold uppercase tracking-wider text-danger">Danger zone</div>
      <p className="mt-1 text-xs text-muted">
        Soft-delete via Archive (reversible — just set status back). Use Reject when you've decided not to write this keyword. Hard Delete is permanent and cascades.
      </p>
      {error && <p className="mt-2 text-xs text-danger">{error}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={archive} disabled={busy !== null || k.status === "archived"} className="btn btn-secondary text-xs">
          {busy === "archive" ? "Archiving…" : k.status === "archived" ? "Already archived" : "Archive"}
        </button>
        <button onClick={reject} disabled={busy !== null} className="btn btn-secondary text-xs">
          {busy === "reject" ? "Rejecting…" : "Reject…"}
        </button>
        <button onClick={hardDelete} disabled={busy !== null} className="btn btn-ghost text-xs text-danger hover:bg-danger/10">
          {busy === "delete" ? "Deleting…" : "Hard delete…"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={wide ? "col-span-2" : undefined}>
      <dt className="text-[10px] uppercase tracking-wide text-muted">{label}</dt>
      <dd className="mt-0.5 text-sm">{children}</dd>
    </div>
  );
}

// ── Human-friendly SERP + Brief views ────────────────────────────────────────
// These render the SAME data the LLM consumes — only the presentation changes.
// The raw JSON stays in the DB and is injected to the production engine verbatim;
// a "View raw JSON" disclosure keeps the exact payload one click away.

function StatTile({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-line bg-canvas px-3 py-2 text-center">
      <div className="text-lg font-semibold text-brand">{value}</div>
      <div className="text-2xs uppercase tracking-wide text-muted">{label}</div>
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return <div className="mb-2 text-2xs font-semibold uppercase tracking-wider text-muted">{children}</div>;
}

function RawJsonDisclosure({ data }: { data: unknown }) {
  return (
    <details className="mt-4 rounded-md border border-line">
      <summary className="cursor-pointer select-none px-3 py-2 text-2xs font-medium text-muted hover:text-ink">View raw JSON</summary>
      <pre className="overflow-x-auto rounded-b-md bg-canvas p-3 text-[11px] leading-relaxed text-ink">{JSON.stringify(data, null, 2)}</pre>
    </details>
  );
}

function prettyUrl(u: string): { domain: string; path: string } {
  try {
    const url = new URL(u);
    return { domain: url.hostname.replace(/^www\./, ""), path: decodeURIComponent(url.pathname + url.search) };
  } catch {
    return { domain: u, path: "" };
  }
}

interface SerpShape {
  aiOverviewPresent?: boolean;
  organicCount?: number;
  paaCount?: number;
  relatedSearchCount?: number;
  paaQuestions?: string[];
  topCompetitors?: string[];
  variants?: string[];
  fetchedAt?: string;
}

function SerpTab({ serp, trends, language }: { serp: unknown; trends: unknown; language: string }) {
  if (!serp) return <div className="text-sm text-muted">No SERP snapshot yet — run Enrich</div>;
  const s = serp as SerpShape;
  const dir = language === "he" ? "rtl" : "ltr";
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className={s.aiOverviewPresent ? "pill-info" : "pill-muted"}>{s.aiOverviewPresent ? "AI Overview present" : "No AI Overview"}</span>
        {s.fetchedAt && <span className="text-2xs text-muted">captured {new Date(s.fetchedAt).toLocaleString()}</span>}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <StatTile label="Organic" value={s.organicCount ?? "—"} />
        <StatTile label="PAA" value={s.paaCount ?? s.paaQuestions?.length ?? "—"} />
        <StatTile label="Related" value={s.relatedSearchCount ?? "—"} />
        <StatTile label="Competitors" value={s.topCompetitors?.length ?? "—"} />
      </div>

      {s.paaQuestions?.length ? (
        <section>
          <SectionHeader>People also ask</SectionHeader>
          <ul className="space-y-1.5" dir={dir}>
            {s.paaQuestions.map((q, i) => <li key={i} className="rounded-md bg-canvas px-3 py-1.5 text-sm">{q}</li>)}
          </ul>
        </section>
      ) : null}

      {s.topCompetitors?.length ? (
        <section>
          <SectionHeader>Top competitors ({s.topCompetitors.length})</SectionHeader>
          <ul className="space-y-1">
            {s.topCompetitors.map((u, i) => {
              const { domain, path } = prettyUrl(u);
              return (
                <li key={i} className="flex items-baseline gap-2 text-sm">
                  <span className="w-5 shrink-0 text-right text-2xs text-muted">{i + 1}.</span>
                  <a href={u} target="_blank" rel="noreferrer" className="min-w-0 truncate text-brand-700 hover:underline" title={u}>
                    <span className="font-medium">{domain}</span><span className="text-muted">{path}</span>
                  </a>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {s.variants?.length ? (
        <section>
          <SectionHeader>Keyword variants ({s.variants.length})</SectionHeader>
          <div className="flex flex-wrap gap-1.5" dir={dir}>{s.variants.map((v, i) => <span key={i} className="pill-muted">{v}</span>)}</div>
        </section>
      ) : null}

      {trends ? (
        <section><SectionHeader>Trends</SectionHeader><RawJsonDisclosure data={trends} /></section>
      ) : null}

      <RawJsonDisclosure data={serp} />
    </div>
  );
}

interface BriefShape {
  primaryKeywordHe?: string | null;
  primaryKeywordEn?: string | null;
  secondaryKeywords?: string[];
  targetIntent?: string | null;
  funnelStage?: string | null;
  cluster?: string | null;
  pillarPage?: boolean;
  competitorAvgWordCount?: number | null;
  targetWordCount?: number | null;
  minWordCount?: number | null;
  outlineH1?: string | null;
  metaTitle?: string | null;
  metaDescription?: string | null;
  excerpt?: string | null;
  metaVariants?: { outlineH1?: string | null; metaTitle?: string | null; metaDescription?: string | null; excerpt?: string | null; source?: string; createdAt?: string }[];
  outlineSections?: { h2: string; h3?: string[]; intent?: string | null; imagePlacement?: boolean; targetWordCount?: number | null; mustCoverPoints?: string[] }[];
  faqSection?: { question: string; answerGuidance?: string | null }[];
  semanticMustCover?: string[];
  competitiveGap?: string | null;
  uniquePerspective?: string | null;
  israelContext?: string | null;
  /** Our own live pages that already cover part of this keyword (brief/site-coverage.ts). */
  siteCoverage?: { title: string; url: string; anchor: string; status?: "live" | "in-flight"; headings?: string[]; overlapping?: string[] }[];
  internalLinks?: { anchorText?: string; targetUrl?: string | null; notes?: string | null }[];
  sources?: { title: string; publisher?: string | null; url?: string | null }[];
  brandTieIns?: string[];
  generationMode?: string;
  llmModel?: string | null;
  warnings?: string[];
}

type MetaVariant = { outlineH1?: string | null; metaTitle?: string | null; metaDescription?: string | null; excerpt?: string | null; source?: string; createdAt?: string };

function MetaVariations({ variants, active, dir, busy, onUse }: {
  variants: MetaVariant[]; active: { outlineH1?: string | null; metaTitle?: string | null; metaDescription?: string | null };
  dir: "rtl" | "ltr"; busy: boolean; onUse: (v: MetaVariant) => void;
}) {
  const [open, setOpen] = useState(false);
  const isActive = (v: MetaVariant) =>
    (v.outlineH1 ?? "") === (active.outlineH1 ?? "") &&
    (v.metaTitle ?? "") === (active.metaTitle ?? "") &&
    (v.metaDescription ?? "") === (active.metaDescription ?? "");
  const ordered = [...variants].reverse(); // newest first
  return (
    <div className="mt-2">
      <button onClick={() => setOpen((o) => !o)} className="text-2xs font-medium text-brand-700 hover:underline">
        {open ? "▾" : "▸"} {variants.length} variations — view &amp; select
      </button>
      {open && (
        <div className="mt-1.5 space-y-1.5">
          {ordered.map((v, i) => {
            const act = isActive(v);
            return (
              <div key={i} className={`rounded-lg border px-2.5 py-1.5 text-xs ${act ? "border-brand-300 bg-brand-50/50" : "border-line bg-canvas"}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-2xs text-muted">
                    <span className="rounded bg-line/60 px-1">{v.source === "brief" ? "brief" : "regen"}</span>
                    {act ? <span className="font-medium text-brand-700">✓ current</span> : null}
                  </span>
                  {!act && <button onClick={() => onUse(v)} disabled={busy} className="btn-ghost btn-sm text-2xs">Use this</button>}
                </div>
                <div className="mt-0.5 space-y-0.5" dir={dir}>
                  {v.outlineH1 && <div><span className="text-2xs text-muted">H1 ({v.outlineH1.length}): </span><span className="text-ink">{v.outlineH1}</span></div>}
                  {v.metaTitle && <div><span className="text-2xs text-muted">Title ({v.metaTitle.length + 13}/60): </span><span className="text-ink">{v.metaTitle}</span></div>}
                  {v.metaDescription && <div className="text-muted"><span className="text-2xs">Desc ({v.metaDescription.length}): </span>{v.metaDescription}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EditableMeta({ label, value, dir, max, min, suffix, busy, textarea, onSave }: {
  label: string; value: string; dir: "rtl" | "ltr"; max: number; min?: number;
  suffix: boolean; busy: boolean; textarea?: boolean; onSave: (v: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const len = draft.length;
  const total = suffix ? len + 13 : len;
  const over = suffix ? total > 60 : len > max;
  const under = min != null && len > 0 && len < min;
  const hint = suffix ? `${len} + " | AlternaBe" (13) = ${total} / 60` : `${len}${min != null ? ` / ${min}–${max}` : ` / ${max}`} chars`;
  const commit = () => { const t = draft.trim(); if (t && t !== value) onSave(t); else if (!t) setDraft(value); };
  return (
    <div className="rounded-lg border border-line bg-canvas px-3 py-2">
      <div className="flex items-center justify-between">
        <span className="text-2xs font-semibold uppercase tracking-wide text-muted">{label}</span>
        <span className={`text-2xs tabular-nums ${over ? "font-medium text-amber-600" : under ? "text-amber-500" : "text-muted"}`}>{over ? "⚠ " : under ? "short · " : ""}{hint}</span>
      </div>
      <div className="mt-0.5 flex items-start gap-1">
        {textarea ? (
          <textarea value={draft} dir={dir} rows={2} disabled={busy} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
            className="w-full resize-none rounded border border-transparent bg-transparent px-1 -mx-1 text-sm text-ink hover:border-line focus:border-brand-400 focus:bg-white focus:outline-none" />
        ) : (
          <input value={draft} dir={dir} disabled={busy} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
            onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") { setDraft(value); (e.target as HTMLInputElement).blur(); } }}
            className="w-full rounded border border-transparent bg-transparent px-1 -mx-1 text-sm font-medium text-ink hover:border-line focus:border-brand-400 focus:bg-white focus:outline-none" />
        )}
        {suffix && <span className="mt-0.5 shrink-0 text-sm text-muted">| AlternaBe</span>}
      </div>
    </div>
  );
}

function AngleBlock({ label, text, dir }: { label: string; text: string; dir: "rtl" | "ltr" }) {
  return (
    <div className="rounded-lg border border-line bg-canvas px-3 py-2">
      <div className="text-2xs font-semibold uppercase tracking-wide text-muted">{label}</div>
      <div className="mt-0.5 text-sm text-ink" dir={dir}>{text}</div>
    </div>
  );
}

function BriefTab({ brief, language, doNotMention, keywordId, onChanged }: {
  brief: unknown; language: string;
  doNotMention: { name: string; count: number; context: string; reason: string | null }[];
  keywordId: string; onChanged: () => void;
}) {
  const patch = useMutation({ mutationFn: (f: { outlineH1?: string; metaTitle?: string; metaDescription?: string; excerpt?: string }) => api.patchBriefFields(keywordId, f), onSuccess: onChanged });
  const regenMeta = useMutation({ mutationFn: () => api.regenerateBriefMeta(keywordId), onSuccess: onChanged });
  if (!brief) return <div className="text-sm text-muted">No brief yet — run Generate Brief</div>;
  const b = brief as BriefShape;
  const dir = language === "he" ? "rtl" : "ltr";
  const dnmNames = doNotMention.map((v) => v.name);
  const flagged = (s: string): boolean => dnmNames.some((n) => s.includes(n));
  return (
    <div className="space-y-5">
      {doNotMention.length > 0 && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 p-3">
          <div className="flex items-center gap-1.5 text-sm font-medium text-danger">
            <AlertTriangle className="h-4 w-4" /> Do-not-mention entities in this brief ({doNotMention.length})
          </div>
          <ul className="mt-1.5 space-y-1 text-xs">
            {doNotMention.map((v) => (
              <li key={v.name} dir={dir}>
                <span className="font-semibold text-danger">{v.name}</span>
                {v.count > 1 && <span className="text-muted"> ×{v.count}</span>}
                <span className="text-muted"> — “…{v.context}…”</span>
              </li>
            ))}
          </ul>
          <p className="mt-1.5 text-2xs text-muted">These are competitors/peers flagged do-not-mention. Regenerate the brief (the generator now excludes them) or edit the outline before producing. If a match is a coincidence (e.g. “כללית” meaning “general”), you can produce past the confirm.</p>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        {b.targetIntent && <span className="pill-info">{b.targetIntent}</span>}
        {b.funnelStage && <span className="pill-muted">{b.funnelStage}</span>}
        {b.pillarPage && <span className="pill-ok">pillar</span>}
        {b.cluster && <span className="pill-muted">cluster: {b.cluster}</span>}
        {b.generationMode && <span className="text-2xs text-muted">{b.generationMode}{b.llmModel ? ` · ${b.llmModel}` : ""}</span>}
      </div>

      <div className="grid grid-cols-3 gap-2">
        <StatTile label="Target words" value={b.targetWordCount ?? "—"} />
        <StatTile label="Min words" value={b.minWordCount ?? "—"} />
        <StatTile label="Competitor avg" value={b.competitorAvgWordCount ?? "—"} />
      </div>

      <div>
        <div className="flex items-center justify-between">
          <SectionHeader>SEO meta &amp; H1</SectionHeader>
          <button onClick={() => regenMeta.mutate()} disabled={regenMeta.isPending || patch.isPending}
            className="btn-ghost btn-sm text-xs" title="Re-generate the H1 + meta from the SERP (competitor titles, PAA, fanout). Costs a fraction of a cent.">
            {regenMeta.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Regenerate from SERP
          </button>
        </div>
        {regenMeta.isError && <p className="mb-1 text-2xs text-danger">{(regenMeta.error as Error).message}</p>}
        <div className="space-y-2">
          <EditableMeta label="H1 (on-page)" value={b.outlineH1 ?? ""} dir={dir} max={60} suffix={false} busy={patch.isPending}
            onSave={(v) => patch.mutate({ outlineH1: v })} />
          <EditableMeta label="Meta title" value={b.metaTitle ?? ""} dir={dir} max={47} suffix busy={patch.isPending}
            onSave={(v) => patch.mutate({ metaTitle: v })} />
          <EditableMeta label="Meta description" value={b.metaDescription ?? ""} dir={dir} min={140} max={160} suffix={false} busy={patch.isPending} textarea
            onSave={(v) => patch.mutate({ metaDescription: v })} />
          <EditableMeta label="Excerpt (on-page lead)" value={b.excerpt ?? ""} dir={dir} min={80} max={200} suffix={false} busy={patch.isPending} textarea
            onSave={(v) => patch.mutate({ excerpt: v })} />
        </div>
        <p className="mt-1 text-2xs text-muted">
          The excerpt is what a reader sees under the H1 — not a SERP snippet. No CTA, no site name. Empty falls back to the meta description on publish.
        </p>
        {(b.metaVariants?.length ?? 0) > 1 && (
          <MetaVariations variants={b.metaVariants!} active={b} dir={dir} busy={patch.isPending}
            onUse={(v) => patch.mutate({ outlineH1: v.outlineH1 ?? undefined, metaTitle: v.metaTitle ?? "", metaDescription: v.metaDescription ?? "", excerpt: v.excerpt ?? "" })} />
        )}
      </div>

      {(b.competitiveGap || b.uniquePerspective || b.israelContext) ? (
        <div className="space-y-2">
          {b.competitiveGap && <AngleBlock label="Competitive gap" text={b.competitiveGap} dir={dir} />}
          {b.uniquePerspective && <AngleBlock label="Unique perspective" text={b.uniquePerspective} dir={dir} />}
          {b.israelContext && <AngleBlock label="Israel context" text={b.israelContext} dir={dir} />}
        </div>
      ) : null}

      {b.outlineSections?.length ? (
        <section>
          <SectionHeader>Outline ({b.outlineSections.length} sections)</SectionHeader>
          <ol className="space-y-2">
            {b.outlineSections.map((sec, i) => (
              <li key={i} className="rounded-lg border border-line p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className={`font-medium ${flagged(sec.h2) ? "text-danger" : "text-ink"}`} dir={dir}>
                    {flagged(sec.h2) && <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />}{i + 1}. {sec.h2}
                  </div>
                  <div className="flex shrink-0 items-center gap-1 text-2xs text-muted">
                    {sec.targetWordCount ? <span>~{sec.targetWordCount}w</span> : null}
                    {sec.imagePlacement ? <span className="pill-muted">image</span> : null}
                  </div>
                </div>
                {sec.intent && <div className="mt-0.5 text-xs italic text-muted" dir={dir}>{sec.intent}</div>}
                {sec.h3?.length ? <div className="mt-1.5 flex flex-wrap gap-1" dir={dir}>{sec.h3.map((h, j) => <span key={j} className="pill-muted">{h}</span>)}</div> : null}
                {sec.mustCoverPoints?.length ? <ul className="mt-1.5 list-inside list-disc space-y-0.5 text-xs text-muted" dir={dir}>{sec.mustCoverPoints.map((p, j) => <li key={j}>{p}</li>)}</ul> : null}
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      {b.faqSection?.length ? (
        <section>
          <SectionHeader>FAQ ({b.faqSection.length})</SectionHeader>
          <ul className="space-y-1.5" dir={dir}>
            {b.faqSection.map((f, i) => (
              <li key={i} className="rounded-md bg-canvas px-3 py-1.5 text-sm">{f.question}{f.answerGuidance && <div className="mt-0.5 text-xs text-muted">{f.answerGuidance}</div>}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {b.semanticMustCover?.length ? (
        <section><SectionHeader>Semantic must-cover</SectionHeader><div className="flex flex-wrap gap-1.5" dir={dir}>{b.semanticMustCover.map((m, i) => <span key={i} className={flagged(m) ? "inline-flex items-center gap-1 rounded-full bg-danger/10 px-2 py-0.5 text-2xs font-medium text-danger" : "pill-info"}>{flagged(m) && <AlertTriangle className="h-3 w-3" />}{m}</span>)}</div></section>
      ) : null}

      {b.secondaryKeywords?.length ? (
        <section><SectionHeader>Secondary keywords</SectionHeader><div className="flex flex-wrap gap-1.5" dir={dir}>{b.secondaryKeywords.map((m, i) => <span key={i} className="pill-muted">{m}</span>)}</div></section>
      ) : null}

      {b.siteCoverage?.length ? (
        // Shown ABOVE the link list on purpose: this is why several of those
        // links are there, and it is the one place the operator can see that the
        // outline was steered away from rewriting our own pages.
        <section>
          <SectionHeader>Already on our site ({b.siteCoverage.length})</SectionHeader>
          <p className="mb-1.5 text-2xs text-muted">
            Our own live pages on this subject. The outline was told not to rewrite a marked section — bridge to it in a sentence and link.
          </p>
          <ul className="space-y-1.5 text-sm" dir={dir}>
            {b.siteCoverage.map((p, i) => (
              <li key={i} className="rounded-md bg-canvas px-3 py-1.5">
                {p.status === "in-flight" || !p.url ? (
                  <span>{p.title} <span className="pill-muted">not published yet</span></span>
                ) : (
                  <a href={p.url} target="_blank" rel="noreferrer" className="text-brand-700 hover:underline">{p.title}</a>
                )}
                {p.overlapping?.length ? (
                  <ul className="mt-1 space-y-0.5">
                    {p.overlapping.map((h, j) => (
                      <li key={j} className="flex items-start gap-1 text-xs text-warn">
                        <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-2xs text-muted">Same subject, no overlapping section</div>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {b.internalLinks?.length ? (
        <section>
          <SectionHeader>Internal links ({b.internalLinks.length})</SectionHeader>
          <ul className="space-y-1 text-sm" dir={dir}>
            {b.internalLinks.map((l, i) => (
              <li key={i}>
                {l.targetUrl ? <a href={l.targetUrl} target="_blank" rel="noreferrer" className="text-brand-700 hover:underline">{l.anchorText || l.targetUrl}</a> : <span>{l.anchorText}</span>}
                {l.notes && <span className="text-2xs text-muted"> ({l.notes})</span>}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {b.sources?.length ? (
        <section>
          <SectionHeader>Sources</SectionHeader>
          <ul className="space-y-0.5 text-sm">
            {b.sources.map((src, i) => (
              <li key={i}>{src.url ? <a href={src.url} target="_blank" rel="noreferrer" className="text-brand-700 hover:underline">{src.title}</a> : src.title}{src.publisher ? <span className="text-muted"> — {src.publisher}</span> : null}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {b.brandTieIns?.length ? (
        <section><SectionHeader>Brand tie-ins</SectionHeader><ul className="list-inside list-disc space-y-0.5 text-xs text-muted" dir={dir}>{b.brandTieIns.map((t, i) => <li key={i}>{t}</li>)}</ul></section>
      ) : null}

      {b.warnings?.length ? (
        <div className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-xs text-amber-800">{b.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}</div>
      ) : null}

      <RawJsonDisclosure data={brief} />
    </div>
  );
}

const IMG_PLACEHOLDER = "alternabe-image-placeholder";

/**
 * Render the produced article markdown as formatted content (not raw md).
 *
 * Exported so the selection editor can render each BLOCK through the exact same
 * renderer. Blocks must not look different from the whole document — the image
 * placeholders, the RTL handling and the custom element styling all live here,
 * and a second plain-ReactMarkdown path would quietly drop them.
 */
export function ArticleMarkdown({ markdown, dir }: { markdown: string; dir: "rtl" | "ltr" }) {
  // {{IMAGE: alt}} markers (pre-image-gen) → block images our renderer styles as
  // placeholders. Real images keep their ![alt](src).
  const prepared = markdown.replace(/\{\{IMAGE:\s*([^}]*)\}\}/g, (_m, alt: string) => `\n\n![${alt.trim()}](${IMG_PLACEHOLDER})\n\n`);
  return (
    <div dir={dir} className="text-sm leading-relaxed text-ink">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => <h1 className="mb-2 mt-4 text-xl font-bold text-ink">{children}</h1>,
          h2: ({ children }) => <h2 className="mb-2 mt-5 border-b border-line pb-1 text-lg font-semibold text-ink">{children}</h2>,
          h3: ({ children }) => <h3 className="mb-1 mt-3 text-base font-semibold text-ink">{children}</h3>,
          p: ({ children }) => <p className="my-2">{children}</p>,
          ul: ({ children }) => <ul className="my-2 list-inside list-disc space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="my-2 list-inside list-decimal space-y-1">{children}</ol>,
          li: ({ children }) => <li>{children}</li>,
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          a: ({ href, children }) => <a href={href} target="_blank" rel="noreferrer" className="text-brand-700 underline">{children}</a>,
          blockquote: ({ children }) => <blockquote className="my-3 rounded-md bg-canvas px-3 py-2 italic text-muted">{children}</blockquote>,
          hr: () => <hr className="my-4 border-line" />,
          table: ({ children }) => <div className="my-3 overflow-x-auto"><table className="min-w-full border border-line text-xs">{children}</table></div>,
          th: ({ children }) => <th className="border border-line bg-canvas px-2 py-1 text-start font-semibold">{children}</th>,
          td: ({ children }) => <td className="border border-line px-2 py-1">{children}</td>,
          img: ({ src, alt }) => {
            if (typeof src === "string" && /^https?:\/\//.test(src)) {
              return <img src={src} alt={alt ?? ""} className="my-3 max-w-full rounded-lg border border-line" />;
            }
            return (
              <span className="my-3 flex items-center gap-2 rounded-lg border border-dashed border-line bg-canvas px-3 py-3 text-xs text-muted">
                <ImageIcon className="h-4 w-4 shrink-0" />
                <span>{alt || "image"}{src && src !== IMG_PLACEHOLDER ? ` · ${src}` : " — placeholder, filled at the Images step"}</span>
              </span>
            );
          },
        }}
      >
        {prepared}
      </ReactMarkdown>
    </div>
  );
}

function relativeTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const diffMs = Date.now() - t;
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

function SeoScoreCard({ keywordId }: { keywordId: string }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["seo-score", keywordId], queryFn: () => api.getSeoScore(keywordId) });
  const s = data?.score;
  if (isLoading) return <div className="card p-3 text-xs text-muted">Scoring…</div>;
  if (!s) return null;
  const tone = s.score >= 85 ? "text-emerald-700" : s.score >= 70 ? "text-brand-700" : s.score >= 50 ? "text-amber-700" : "text-danger";
  const dot = (st: string) => st === "pass" ? "bg-emerald-500" : st === "warn" ? "bg-amber-500" : "bg-danger";
  const problems = s.checks.filter((c) => c.status !== "pass");
  return (
    <div className="card p-3">
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between gap-3 text-left">
        <div className="flex items-baseline gap-2">
          <span className={`font-mono text-lg font-semibold ${tone}`}>{s.score}</span>
          <span className="text-2xs text-muted">/100 · {s.grade}</span>
          <span className="text-2xs text-muted">· {s.wordCount} words · head-term {s.headTermDensityPct}%</span>
        </div>
        <div className="flex items-center gap-2">
          {problems.length > 0 && <span className="text-2xs text-muted">{problems.length} to fix</span>}
          <span className="flex gap-0.5">
            {s.checks.map((c) => <span key={c.id} className={`h-1.5 w-1.5 rounded-full ${dot(c.status)}`} title={`${c.label}: ${c.detail}`} />)}
          </span>
          <span className="text-muted">{open ? "▾" : "▸"}</span>
        </div>
      </button>
      {open && (
        <div className="mt-2 space-y-1 border-t border-line pt-2">
          {s.checks.map((c) => (
            <div key={c.id} className="flex items-start gap-2 text-2xs">
              <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${dot(c.status)}`} />
              <div className="min-w-0 flex-1">
                <span className="font-medium">{c.label}</span>
                <span className="text-muted"> · {c.detail}</span>
                {c.fix && <div className="text-amber-700" dir="auto">→ {c.fix}</div>}
              </div>
              <span className="shrink-0 font-mono text-muted">{c.earned}/{c.max}</span>
            </div>
          ))}
          <p className="pt-1 text-2xs text-muted">
            Density is measured on the prefix-folded head term as a floor + over-optimisation ceiling — not a 1–2% exact-phrase target.
          </p>
        </div>
      )}
    </div>
  );
}

function ArticleTab({ detail, keywordId, onChanged }: { detail: KeywordDetail; keywordId: string; onChanged: () => void }) {
  const [showRaw, setShowRaw] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);
  /** Draft as it was before the last accepted passage edit — the undo target. */
  const [undo, setUndo] = useState<string | null>(null);
  const md = detail.keyword.articleMarkdown;

  const save = useMutation({
    mutationFn: () => api.patchArticleBody(keywordId, draft),
    onSuccess: (r) => {
      if (!r.ok) { setBanner({ ok: false, text: r.error ?? "Save failed" }); return; }
      setBanner({ ok: true, text: r.summary ?? "Saved." });
      setEditing(false);
      onChanged();
    },
    onError: (e: Error) => setBanner({ ok: false, text: e.message }),
  });

  // "Run links" — weave must-have + related internal links into the current draft.
  // Result lands in the editor to review; Save persists it (and keeps the links).
  const weave = useMutation({
    mutationFn: () =>
      api.weaveLinks({
        keyword: detail.keyword.keyword,
        bodyMarkdown: draft,
        language: detail.keyword.language as "he" | "en",
        ...(detail.keyword.clusterId ? { clusterId: detail.keyword.clusterId } : {}),
      }),
    onSuccess: (r) => {
      if (!r.ok) { setBanner({ ok: false, text: r.error ?? "Run links failed" }); return; }
      setDraft(r.bodyMarkdown);
      setBanner({ ok: true, text: r.summary });
    },
    onError: (e: Error) => setBanner({ ok: false, text: e.message }),
  });

  /**
   * "Repair" — the passes production now runs after writing, for an article
   * written before they existed: link our own pages on this subject (required
   * links) and proofread the Hebrew. The result opens in the editor so any
   * added bridging sentence is read before Save.
   */
  const repair = useMutation({
    mutationFn: () => api.repairArticle(keywordId),
    onSuccess: (r) => {
      if (!r.ok || typeof r.bodyMarkdown !== "string") { setBanner({ ok: false, text: r.error ?? "Repair failed" }); return; }
      setUndo(md ?? null);
      setDraft(r.bodyMarkdown);
      setEditing(true);
      const notes = (r.warnings ?? []).join(" ");
      setBanner({
        ok: true,
        text:
          `${r.summary ?? "Repaired"}. Review the changes here, then Save body.` +
          (r.metaChanged ? " A Hebrew fix to the SEO title/description was also found — apply it in the Brief tab." : "") +
          (notes ? ` ${notes}` : ""),
      });
    },
    onError: (e: Error) => setBanner({ ok: false, text: e.message }),
  });

  if (!md) {
    return <div className="text-sm text-muted">No article produced yet. Run “Produce article” after Brief is set.</div>;
  }
  const dir = detail.keyword.language === "he" ? "rtl" : "ltr";
  const producedAt = detail.keyword.producedAt ?? detail.keyword.lastReviewedAt;
  const rel = relativeTime(producedAt);
  const startEdit = () => { setDraft(md); setBanner(null); setEditing(true); };

  /**
   * A passage edit lands in the SAME draft the manual editor uses, rather than
   * saving straight through. The operator gets one review surface and one Save,
   * and an AI rewrite is not privileged over a hand edit.
   *
   * The pre-edit text is kept so an ACCEPTED change can still be walked back.
   * Reject covers "I can see it is wrong"; this covers "I accepted it, then read
   * it in context and changed my mind", which is the more common one.
   */
  const applyPassageEdit = (next: string) => {
    setUndo(editing ? draft : md);
    setDraft(next);
    setEditing(true);
    setBanner({ ok: true, text: "Passage rewritten into the draft. Review it, then Save — or undo it." });
  };

  return (
    <div className="space-y-3">
      <SeoScoreCard keywordId={keywordId} />
      <MaestroHandoff keywordId={keywordId} detail={detail} onChanged={onChanged} />
      <div className="flex items-center justify-between gap-2">
        <div className="text-2xs text-muted">
          {producedAt ? <>Produced <span title={new Date(producedAt).toLocaleString()}>{rel}</span> · {new Date(producedAt).toLocaleString()}</> : "Production time unknown"}
        </div>
        {!editing && (
          <div className="flex items-center gap-3">
            <button onClick={startEdit} className="inline-flex items-center gap-1 text-2xs font-medium text-brand hover:brightness-95">
              <Pencil className="h-3 w-3" /> Edit body
            </button>
            <button
              onClick={() => { setBanner(null); repair.mutate(); }}
              disabled={repair.isPending}
              className="inline-flex items-center gap-1 text-2xs font-medium text-brand hover:brightness-95 disabled:opacity-50"
              title="Link our own pages on this subject that the article skipped, and proofread the Hebrew. Opens the result in the editor to review before Save."
            >
              {repair.isPending ? <><Loader2 className="h-3 w-3 animate-spin" /> Repairing…</> : <><Link2 className="h-3 w-3" /> Repair links + Hebrew</>}
            </button>
            <button onClick={() => setShowRaw((v) => !v)} className="text-2xs font-medium text-muted hover:text-ink">
              {showRaw ? "Show formatted" : "Show raw markdown"}
            </button>
          </div>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={() => { setBanner(null); weave.mutate(); }}
              disabled={weave.isPending || save.isPending || draft.trim().length < 30}
              className="btn-ghost text-2xs disabled:opacity-50"
              title="Insert our must-have links + related internal links into the body. Review here, then Save."
            >
              {weave.isPending ? <><Loader2 className="h-3 w-3 animate-spin" /> Running links…</> : <><Link2 className="h-3 w-3" /> Run links</>}
            </button>
          </div>
          <RichTextEditor
            value={draft}
            onChange={setDraft}
            minHeight={360}
            placeholder="Edit the article body. Use the toolbar for headings, lists, and links."
            onUploadImage={async (dataUrl) => {
              const r = await api.uploadImage(dataUrl);
              if (!r.ok || !r.url) { setBanner({ ok: false, text: r.error ?? "Image upload failed" }); return null; }
              return r.url;
            }}
          />
          <div className="flex items-center justify-between gap-2">
            <div className="text-2xs text-muted">Edits the article body. Re-publish (or Regenerate → Publish) to push to Sanity. The H1 + meta live in the Brief tab.</div>
            <div className="flex shrink-0 items-center gap-2">
              <button className="btn-ghost text-xs" onClick={() => { setEditing(false); setBanner(null); }} disabled={save.isPending}>Cancel</button>
              <button className="btn-primary text-xs" onClick={() => save.mutate()} disabled={save.isPending || draft.trim().length < 1}>
                {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Save body
              </button>
            </div>
          </div>
        </div>
      ) : showRaw ? (
        <pre className="whitespace-pre-wrap break-words rounded-md bg-canvas p-3 text-[12px] leading-relaxed text-ink" dir={dir}>
          {md}
        </pre>
      ) : (
        <SelectionEditor keywordId={keywordId} markdown={md} dir={dir} onApplied={applyPassageEdit} />
      )}

      {banner && (
        <div className={`flex items-center gap-2 rounded-md px-2.5 py-1.5 text-2xs ${banner.ok ? "bg-accent/10 text-accent" : "bg-danger/10 text-danger"}`} dir="auto">
          <span>{banner.text}</span>
          {undo !== null && editing && (
            <button
              type="button"
              onClick={() => { setDraft(undo); setUndo(null); setBanner({ ok: true, text: "Reverted to the text before that edit." }); }}
              className="ml-auto shrink-0 font-medium underline hover:no-underline"
            >
              Undo that edit
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface StoryboardView {
  totalSec: number;
  aspectRatio: string;
  h1: string;
  hook: string;
  cta: string | null;
  scenes: { durationSec: number; imagePrompt: string; hebrewCaption: string; narration: string }[];
  generatedAt: string;
  modelUsed: string;
  estimatedCostUsd: number | null;
}

function StoryboardArtifact({ sb }: { sb: StoryboardView }) {
  const [open, setOpen] = useState(false);
  const rel = relativeTime(sb.generatedAt);
  return (
    <div className="rounded-md border border-line bg-white">
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left">
        <div className="flex items-center gap-2">
          <span className="pill pill-ok">storyboard</span>
          <span className="text-sm font-medium">{sb.scenes.length} scenes · {sb.totalSec}s · {sb.aspectRatio}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted">
          <span>{sb.modelUsed}{sb.estimatedCostUsd != null ? ` · $${sb.estimatedCostUsd.toFixed(4)}` : ""}{rel ? ` · ${rel}` : ""}</span>
          <span>{open ? "▾" : "▸"}</span>
        </div>
      </button>
      {open && (
        <div className="space-y-2 border-t border-line px-3 py-2.5">
          {sb.hook && <div className="text-xs" dir="auto"><span className="text-muted">Hook: </span>{sb.hook}</div>}
          <ol className="space-y-2">
            {sb.scenes.map((s, i) => (
              <li key={i} className="rounded border border-line/60 p-2 text-xs" dir="auto">
                <div className="mb-1 font-mono text-2xs text-muted">scene {i + 1} · {s.durationSec}s</div>
                <div className="font-medium">{s.hebrewCaption}</div>
                <div className="mt-0.5 text-muted">{s.narration}</div>
                <div className="mt-1 text-2xs text-muted" dir="ltr">🎨 {s.imagePrompt}</div>
              </li>
            ))}
          </ol>
          {sb.cta && <div className="text-xs" dir="auto"><span className="text-muted">CTA: </span>{sb.cta}</div>}
        </div>
      )}
    </div>
  );
}

function AssetsTab({ keywordId, detail }: { keywordId: string; detail: KeywordDetail }) {
  // Live in-progress generations (keyed mutations in the toolbar). Drives the
  // "generating…" cards + a faster refetch so the finished asset appears promptly.
  const storyboardRunning = useIsMutating({ mutationKey: ["gen", keywordId, "storyboard"] }) > 0;
  const voiceoverRunning = useIsMutating({ mutationKey: ["gen", keywordId, "voiceover"] }) > 0;
  const viralRunning = useIsMutating({ mutationKey: ["gen", keywordId, "viral"] }) > 0;
  const genRunning = [
    storyboardRunning ? { key: "storyboard", label: "Storyboard", note: "Sonnet → scenes" } : null,
    voiceoverRunning ? { key: "voiceover", label: "Voiceover", note: "ElevenLabs MP3" } : null,
    viralRunning ? { key: "viral", label: "Viral video", note: "render ~5 min" } : null,
  ].filter((g): g is { key: string; label: string; note: string } => g !== null);
  const anyRunning = genRunning.length > 0;

  const qc = useQueryClient();
  const { data, isPending, error } = useQuery({
    queryKey: ["pipeline", "keyword", keywordId, "assets"],
    queryFn: () => api.listKeywordAssets(keywordId),
    refetchInterval: anyRunning ? 5_000 : 30_000, // poll faster while a generation is running
  });

  // ── Per-image generate/regenerate (+ prompt editing) ───────────────────────
  // One editor open at a time (keyed by asset id for existing images, or by slot
  // key for not-yet-generated ones). Generate/regenerate just that slot, optionally
  // with an edited prompt. Poll the image-gen job so the table refreshes on land.
  const [editingKey, setEditingKey] = useState<string | number | null>(null);
  const [draftPrompt, setDraftPrompt] = useState("");
  /** Slot key currently having a prompt written for it, or null. */
  const [suggesting, setSuggesting] = useState<string | null>(null);
  const [regenBanner, setRegenBanner] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  const imgJob = useQuery({
    queryKey: ["images-status", keywordId],
    queryFn: () => api.getImagesStatus(keywordId),
    refetchInterval: (q) => (q.state.data?.status === "generating" ? 2000 : false),
  }).data;
  const imagesRegenerating = imgJob?.status === "generating";
  const prevImg = useRef<string | undefined>(undefined);
  useEffect(() => {
    const s = imgJob?.status;
    if (prevImg.current === "generating" && (s === "done" || s === "error")) {
      qc.invalidateQueries({ queryKey: ["pipeline", "keyword", keywordId, "assets"] });
      setEditingKey(null);
      const warn = imgJob?.summary?.warnings?.length ?? 0;
      setRegenBanner(
        s === "done"
          ? { kind: "ok", text: `Image ready${warn ? ` · ${warn} warning(s)` : ""}. Re-publish to push it live.` }
          : { kind: "error", text: imgJob?.error ?? "Image generation failed" },
      );
    }
    prevImg.current = s;
  }, [imgJob?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  type SlotFlags = { hero?: boolean; social?: boolean; thumbnail?: boolean; sections?: number[] };
  /** Map an existing asset to its selective slot flags + prompt-override key. Null = not a regenerable image. */
  const slotFor = (a: KeywordAssetView): { flags: SlotFlags; key: string } | null => {
    if (a.kind === "hero") return { flags: { hero: true }, key: "hero" };
    if (a.kind === "social_card") return { flags: { social: true }, key: "social" };
    if (a.kind === "thumbnail") return { flags: { thumbnail: true }, key: "thumbnail" };
    if (a.kind === "in_content" && a.sectionIndex != null) return { flags: { sections: [a.sectionIndex] }, key: `in_content:${a.sectionIndex}` };
    return null;
  };

  /**
   * Ask the server to write a prompt for this slot and drop it in the textarea.
   * Never generates an image — the operator still presses Generate.
   */
  const suggestPrompt = async (slot: string) => {
    setSuggesting(slot);
    setRegenBanner(null);
    try {
      const r = await api.suggestImagePrompt(keywordId, slot);
      if (!r.ok || !r.prompt) throw new Error(r.error ?? "could not write a prompt");
      setDraftPrompt(r.prompt);
      // The banner only speaks ok/error; a stripped text-request is worth showing,
      // and "ok" with the explanation reads correctly here.
      if (r.warnings?.length) setRegenBanner({ kind: "ok", text: r.warnings.join(" · ") });
    } catch (e) {
      setRegenBanner({ kind: "error", text: (e as Error).message });
    } finally {
      setSuggesting(null);
    }
  };

  /** Generate/regenerate ONE slot (existing or not-yet-made) with an optional prompt override. */
  const generateSlot = async (flags: SlotFlags, key: string, prompt: string) => {
    if (imagesRegenerating) return;
    setRegenBanner(null);
    try {
      const r = await api.regenerateImages(keywordId, {
        mode: "selective",
        ...flags,
        ...(prompt.trim() ? { promptOverrides: { [key]: prompt.trim() } } : {}),
      });
      if (!r.ok) throw new Error(r.error ?? "Image generation failed");
      // Kick the poll so the "generating…" state shows immediately.
      setTimeout(() => qc.invalidateQueries({ queryKey: ["images-status", keywordId] }), 300);
    } catch (e) {
      setRegenBanner({ kind: "error", text: (e as Error).message });
    }
  };

  const k = detail.keyword;
  const storyboard = k.storyboard as StoryboardView | null;
  const producedAt = k.producedAt ?? k.lastReviewedAt;

  // Artifacts that live on the keyword record (not file rows): article + storyboard.
  const artifacts = (
    <div className="space-y-2">
      {k.articleMarkdown && (
        <div className="flex items-center justify-between rounded-md border border-line bg-white px-3 py-2.5">
          <div className="flex items-center gap-2">
            <span className="pill pill-ok">article</span>
            <span className="text-sm font-medium">{k.articleMarkdown.split(/\s+/).filter(Boolean).length} words</span>
          </div>
          <span className="text-xs text-muted">{producedAt ? `produced ${relativeTime(producedAt)}` : "produced"} · see Article tab</span>
        </div>
      )}
      {storyboard && Array.isArray(storyboard.scenes) && <StoryboardArtifact sb={storyboard} />}
    </div>
  );

  if (isPending) return <div className="text-sm text-muted">Loading assets…</div>;
  if (error) return <div className="text-sm text-danger">{(error as Error).message}</div>;

  const fileAssets = data?.assets ?? [];
  const hasArtifacts = Boolean(k.articleMarkdown) || Boolean(storyboard);

  const runningCards = anyRunning && (
    <div className="space-y-2">
      {genRunning.map((g) => (
        <div key={g.key} className="flex items-center gap-2 rounded-md border border-brand/30 bg-brand/5 px-3 py-2.5">
          <div className="h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-brand border-t-transparent" />
          <span className="pill pill-muted">in progress</span>
          <span className="text-sm font-medium">{g.label}</span>
          <span className="text-xs text-muted">generating… ({g.note})</span>
        </div>
      ))}
    </div>
  );

  if (fileAssets.length === 0 && !hasArtifacts && !anyRunning) {
    return (
      <div className="text-sm text-muted">
        No assets generated yet. After producing the article, run <code className="rounded bg-line/40 px-1">images generate</code>,
        {" "}<code className="rounded bg-line/40 px-1">voiceover</code>, or the storyboard → viral pipeline from this panel.
      </div>
    );
  }
  const counts = data?.countsByStatus ?? { uploaded: 0, partial: 0, failed: 0, pending: 0, skipped: 0 };
  return (
    <div className="space-y-3">
      {runningCards}
      {hasArtifacts && artifacts}
      {fileAssets.length === 0 ? (
        <div className="text-xs text-muted">No rendered files yet (images / voiceover / video). Generate them from the toolbar above.</div>
      ) : (
      <>
      <div className="flex flex-wrap gap-2 text-xs">
        {(["uploaded", "partial", "failed", "pending", "skipped"] as const).map((s) =>
          counts[s] ? (
            <span key={s} className={`pill ${
              s === "uploaded" ? "pill-ok" :
              s === "partial" ? "pill-warn" :
              s === "failed" ? "pill-danger" :
              "pill-muted"
            }`}>
              {s}: {counts[s]}
            </span>
          ) : null,
        )}
      </div>
      {regenBanner && (
        <div className={`rounded-md px-3 py-2 text-xs ${regenBanner.kind === "ok" ? "bg-accent/10 text-accent" : "bg-danger/10 text-danger"}`}>
          {regenBanner.text}
        </div>
      )}
      {imagesRegenerating && (
        <div className="flex items-center gap-2 rounded-md border border-brand/30 bg-brand/5 px-3 py-2 text-xs">
          <div className="h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-brand border-t-transparent" />
          Regenerating image… (a new file + fresh Sanity upload)
        </div>
      )}
      <table className="w-full text-sm">
        <thead className="border-b border-line text-left text-xs uppercase tracking-wide text-muted">
          <tr>
            <th className="py-2 pr-3">Kind</th>
            <th className="py-2 pr-3">File</th>
            <th className="py-2 pr-3">Status</th>
            <th className="py-2 pr-3">Size</th>
            <th className="py-2 pr-3">Sanity</th>
            <th className="py-2 pr-3">Drive</th>
            <th className="py-2 pr-3">Regenerate</th>
          </tr>
        </thead>
        <tbody>
          {data.assets.map((a) => {
            const slot = slotFor(a);
            const editing = editingKey === a.id;
            return (
            <Fragment key={a.id}>
            <tr className="border-b border-line/60 align-top">
              <td className="py-2 pr-3 font-mono text-xs">
                {a.kind}
                {a.sectionIndex !== null && <span className="text-muted"> · {a.sectionIndex}</span>}
              </td>
              <td className="py-2 pr-3">
                <div className="font-medium">{a.filename}</div>
                {a.lastError && <div className="mt-1 text-xs text-danger">{a.lastError}</div>}
              </td>
              <td className="py-2 pr-3">
                <span className={`pill ${
                  a.status === "uploaded" ? "pill-ok" :
                  a.status === "partial" ? "pill-warn" :
                  a.status === "failed" ? "pill-danger" :
                  "pill-muted"
                }`}>
                  {a.status}
                </span>
              </td>
              <td className="py-2 pr-3 text-xs text-muted">
                {a.byteSize ? formatBytes(a.byteSize) : "—"}
              </td>
              <td className="py-2 pr-3">
                {a.sanityUrl ? (
                  <a href={a.sanityUrl} target="_blank" rel="noreferrer" className="text-brand hover:underline">open ↗</a>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
              <td className="py-2 pr-3">
                {a.driveViewUrl ? (
                  <a href={a.driveViewUrl} target="_blank" rel="noreferrer" className="text-brand hover:underline">open ↗</a>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
              <td className="py-2 pr-3">
                {slot ? (
                  <button
                    className="text-brand hover:underline disabled:opacity-40"
                    disabled={imagesRegenerating}
                    onClick={() => { setEditingKey(editing ? null : a.id); setDraftPrompt(a.prompt ?? ""); setRegenBanner(null); }}
                  >
                    {editing ? "close" : "edit / regen"}
                  </button>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
            </tr>
            {editing && slot && (
              <tr className="border-b border-line/60 bg-line/5">
                <td colSpan={7} className="px-3 py-3">
                  <label className="mb-1 block text-xs font-medium text-muted">
                    Prompt sent to the image model (English works best; describe the SUBJECT — never ask for text/letters in the image)
                  </label>
                  <textarea
                    className="w-full rounded-md border border-line bg-white px-2 py-1.5 font-mono text-xs"
                    rows={4}
                    dir="auto"
                    value={draftPrompt}
                    onChange={(e) => setDraftPrompt(e.target.value)}
                    placeholder="No stored prompt yet — leave blank to use the default, or describe the image subject…"
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {/* Same Suggest affordance as the on-demand slots below. This is
                        the editor for images that ALREADY exist — the hero included —
                        so it is the one an operator reaches for most. */}
                    <button
                      className="rounded border border-line px-3 py-1.5 text-xs font-medium hover:bg-line/10 disabled:opacity-40"
                      disabled={imagesRegenerating || suggesting === slot.key}
                      onClick={() => suggestPrompt(slot.key)}
                      title="Write a fresh prompt for this image with AI"
                    >
                      {suggesting === slot.key ? "Writing…" : draftPrompt.trim() ? "✨ Suggest another" : "✨ Suggest a prompt"}
                    </button>
                    <button
                      className="rounded bg-brand px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-40"
                      disabled={imagesRegenerating}
                      onClick={() => generateSlot(slot.flags, slot.key, draftPrompt)}
                    >
                      {imagesRegenerating ? "Regenerating…" : "Regenerate this image (~4 credits)"}
                    </button>
                    <button className="text-xs text-muted hover:underline" onClick={() => setEditingKey(null)}>Cancel</button>
                    <span className="text-xs text-muted">Blank = default prompt. Produces a new image + fresh Sanity upload; re-publish to push it live.</span>
                  </div>
                </td>
              </tr>
            )}
            </Fragment>
            );
          })}
        </tbody>
      </table>
      {/* On-demand slots the article does NOT generate automatically (distribution
          assets). Shown so the operator can create them when actually needed. */}
      {(() => {
        const existingKinds = new Set<string>(fileAssets.map((a) => a.kind));
        const optional = [
          { kind: "social_card", key: "social", label: "Social card", note: "for Facebook / Instagram / Telegram", flags: { social: true } as SlotFlags },
          { kind: "thumbnail", key: "thumbnail", label: "YouTube thumbnail", note: "for YouTube", flags: { thumbnail: true } as SlotFlags },
        ].filter((s) => !existingKinds.has(s.kind));
        if (optional.length === 0) return null;
        return (
          <div className="mt-3 rounded-md border border-dashed border-line p-3">
            <div className="mb-2 text-xs font-medium text-muted">Available to generate on demand (not created automatically — saves image credits)</div>
            <div className="space-y-2">
              {optional.map((s) => {
                const editing = editingKey === s.key;
                return (
                  <div key={s.key}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm">
                        <span className="font-medium">{s.label}</span>
                        <span className="text-xs text-muted"> · {s.note}</span>
                      </div>
                      <button
                        className="text-brand hover:underline disabled:opacity-40"
                        disabled={imagesRegenerating}
                        onClick={() => { setEditingKey(editing ? null : s.key); setDraftPrompt(""); setRegenBanner(null); }}
                      >
                        {editing ? "close" : "generate"}
                      </button>
                    </div>
                    {editing && (
                      <div className="mt-2 rounded-md bg-line/5 p-2">
                        <textarea
                          className="w-full rounded-md border border-line bg-white px-2 py-1.5 font-mono text-xs"
                          rows={3}
                          dir="auto"
                          value={draftPrompt}
                          onChange={(e) => setDraftPrompt(e.target.value)}
                          placeholder="Leave blank for the default prompt, or describe the subject (no text/letters in the image)…"
                        />
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {/* Writing a good prompt means describing a photograph, in
                              English, in the house style, never asking for text. This
                              proposes one; the operator edits or ignores it. Costs a
                              short model call, never an image. */}
                          <button
                            className="rounded border border-line px-3 py-1.5 text-xs font-medium hover:bg-line/10 disabled:opacity-40"
                            disabled={imagesRegenerating || suggesting === s.key}
                            onClick={() => suggestPrompt(s.key)}
                            title="Write a fresh prompt for this image with AI"
                          >
                            {suggesting === s.key ? "Writing…" : draftPrompt.trim() ? "✨ Suggest another" : "✨ Suggest a prompt"}
                          </button>
                          <button
                            className="rounded bg-brand px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-40"
                            disabled={imagesRegenerating}
                            onClick={() => generateSlot(s.flags, s.key, draftPrompt)}
                          >
                            {imagesRegenerating ? "Generating…" : "Generate (~4 credits)"}
                          </button>
                          <button className="text-xs text-muted hover:underline" onClick={() => setEditingKey(null)}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
      </>
      )}
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}KB`;
  return `${(n / 1024 / 1024).toFixed(2)}MB`;
}

function ExtractsTab({ detail }: { detail: KeywordDetail }) {
  if (detail.extracts.length === 0) {
    return <div className="text-sm text-muted">No competitor extracts yet. Run “Scrape competitors” after Enrich.</div>;
  }
  return (
    <ul className="space-y-2">
      {detail.extracts.map((e, i) => (
        <li key={i} className="rounded-md border border-line p-3 text-sm">
          <div className="flex items-center justify-between gap-2">
            <a href={e.url} target="_blank" rel="noreferrer" className="truncate font-medium text-brand hover:underline">
              {e.title ?? e.url}
            </a>
            <span className={`pill ${e.status === "ok" ? "pill-ok" : "pill-danger"}`}>{e.status}</span>
          </div>
          <div className="mt-1 truncate text-xs text-muted">{e.url}</div>
          <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-muted">
            {e.publisher && <span>{e.publisher}</span>}
            {e.wordCount !== null && <span>{e.wordCount}w</span>}
            <span>H1/H2/H3: {e.h1Count ?? 0}/{e.h2Count ?? 0}/{e.h3Count ?? 0}</span>
            <span>{new Date(e.fetchedAt).toLocaleString("en-GB")}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}
